// // File Location: src/services/applicationService.js

import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDoc,
  arrayUnion,
  arrayRemove,
  Timestamp,
  collectionGroup,
  runTransaction,
  setDoc,
  getFirestore,
  serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from './firebaseConfig';
import jsPDF from 'jspdf';
import getFullPatientName from '../utils/patientName';
import { sendEmail } from './notificationService';
import { getPatientApplicationLinkHtml } from '../utils/emailUtils';
import { sendApplicationCreatedPushNotification } from './pushNotificationService';
import { calculateBucketFilter, updateRoleBucket } from './bucketFilterService';

// Create new patient application
// export const createPatientApplication = async (applicationData, documents, hospitalId) => {
//   try {
//     // Upload all documents first
//     const documentUrls = {};
//     const uploadPromises = [];

//     for (const [docType, file] of Object.entries(documents)) {
//       if (file) {
//         const uploadPromise = uploadApplicationDocument(file, docType, applicationData.patientName, hospitalId);
//         uploadPromises.push(
//           uploadPromise.then(url => {
//             documentUrls[docType] = {
//               url,
//               fileName: file.name,
//               size: file.size,
//               uploadedAt: new Date()
//             };
//           })
//         );
//       }
//     }

//     // Wait for all document uploads to complete
//     await Promise.all(uploadPromises);

//     // Prepare application data for Firestore
//     const applicationRecord = {
//       // Application metadata
//       applicationId: generateApplicationId(),
//       hospitalId: hospitalId,
//       status: 'submitted', // submitted, under_review, approved, rejected, pending_documents
//       priority: applicationData.priority || 'normal', // normal, high, urgent
//       emergency: applicationData.emergency || false,
//       submittedAt: new Date(),
//       updatedAt: new Date(),

//       // Patient Information
//       patient: {
//         name: applicationData.patientName,
//         age: parseInt(applicationData.patientAge),
//         gender: applicationData.patientGender,
//         address: applicationData.patientAddress,
//         phone: applicationData.patientPhone,
//         email: applicationData.patientEmail || '',

//         // Patient KYC
//         kyc: {
//           aadharNumber: applicationData.patientAadhar,
//           panNumber: applicationData.patientPan,
//           addressProof: applicationData.patientAddressProof
//         }
//       },

//       // Policy Holder Information
//       policyHolder: {
//         isSelf: applicationData.isSelf,
//         name: applicationData.policyHolderName,
//         age: applicationData.isSelf ? parseInt(applicationData.patientAge) : parseInt(applicationData.policyHolderAge),
//         gender: applicationData.policyHolderGender,
//         address: applicationData.policyHolderAddress,
//         phone: applicationData.policyHolderPhone,
//         email: applicationData.policyHolderEmail || '',
//         relationshipWithPatient: applicationData.relationshipWithPatient,

//         // Policy Holder KYC
//         kyc: {
//           aadharNumber: applicationData.policyHolderAadhar,
//           panNumber: applicationData.policyHolderPan,
//           addressProof: applicationData.policyHolderAddressProof
//         },

//         // Nominee Information
//         nominee: {
//           name: applicationData.nomineeName,
//           relationshipWithNominee: applicationData.relationshipWithNominee,
//           contact: applicationData.nomineeContact
//         }
//       },

//       // Medical Information
//       medical: {
//         diagnosis: applicationData.diagnosis,
//         treatmentType: applicationData.treatmentType, // medical, surgical
//         procedureDetails: applicationData.procedureDetails,
//         doctor: {
//           name: applicationData.doctorName,
//           designation: applicationData.doctorDesignation
//         },
//         consultationsGiven: applicationData.consultationsGiven,
//         admissionDate: applicationData.admissionDate,
//         dischargeDate: applicationData.dischargeDate || null,
//         treatments: applicationData.treatments || [],
//         estimatedCost: applicationData.estimatedCost || 0,
//         actualCost: applicationData.actualCost || 0
//       },

//       // Policy Information
//       policy: {
//         type: applicationData.policyType, // retail, corporate
//         number: applicationData.policyNumber,
//         startDate: applicationData.policyStartDate,
//         endDate: applicationData.policyEndDate,
//         insuranceCompany: applicationData.insuranceCompany,
//         tpaName: applicationData.tpaName,
//         coverageAmount: applicationData.coverageAmount || 0
//       },

//       // Documents
//       documents: documentUrls,

//       // Additional Information
//       remarks: applicationData.remarks || '',

//       // Workflow tracking
//       workflow: {
//         medicalVerification: {
//           status: 'pending', // pending, approved, rejected
//           verifiedBy: null,
//           verifiedAt: null,
//           comments: ''
//         },
//         policyVerification: {
//           status: 'pending',
//           verifiedBy: null,
//           verifiedAt: null,
//           comments: ''
//         },
//         kycVerification: {
//           status: 'pending',
//           verifiedBy: null,
//           verifiedAt: null,
//           comments: ''
//         }
//       }
//     };

//     // Save to Firestore
//     const docRef = await addDoc(collection(db, 'patientApplications'), applicationRecord);

//     return {
//       success: true,
//       applicationId: applicationRecord.applicationId,
//       docId: docRef.id,
//       message: 'Application submitted successfully!'
//     };

//   } catch (error) {
//     console.error('Error creating application:', error);
//     return {
//       success: false,
//       error: error.message || 'Failed to create application'
//     };
//   }
// };
export const createPatientApplication = async (applicationData, documents, hospitalId, hospitalName) => {
  try {
    // Upload all documents first
    const documentUrls = {};
    const uploadPromises = [];

    for (const [docType, file] of Object.entries(documents)) {
      if (file) {
        const uploadPromise = uploadApplicationDocument(file, docType, applicationData.patientName, hospitalId);
        uploadPromises.push(
          uploadPromise.then(url => {
            documentUrls[docType] = {
              url,
              fileName: file.name,
              size: file.size,
              uploadedAt: new Date()
            };
          })
        );
      }
    }

    // Wait for all document uploads to complete
    await Promise.all(uploadPromises);

    // Helper: generate case id parts
    const toTwoLetters = (str, fallback = 'XX') => {
      if (!str || typeof str !== 'string') return fallback;
      const cleaned = str.replace(/[^a-zA-Z]/g, '').toUpperCase();
      return (cleaned.slice(0, 2) || fallback);
    };

    const getNextCaseSequence = async () => {
      const counterRef = doc(db, 'counters', 'caseId');
      const nextVal = await runTransaction(db, async (tx) => {
        const snap = await tx.get(counterRef);
        if (!snap.exists()) {
          tx.set(counterRef, { value: 1000 });
          return 1000;
        }
        const current = snap.data()?.value || 1000;
        const updated = current + 1;
        tx.update(counterRef, { value: updated });
        return updated;
      });
      return nextVal;
    };

    const leadCode = applicationData?.leadGenerator?.entityName
      ? toTwoLetters(applicationData.leadGenerator.entityName)
      : 'GN';
    const hospCode = toTwoLetters(hospitalName || 'HN');
    const patientCode = toTwoLetters(applicationData.patientName || 'PN');
    const seq = await getNextCaseSequence();
    const caseId = `SAM-${leadCode}-${hospCode}-${patientCode}-${seq}`;

    // Generate sequential application ID
    const applicationId = await generateApplicationId();

    // Prepare application data for Firestore
    const applicationRecord = {
      // Application metadata
      applicationId: applicationId,
      caseId,
      hospitalId: hospitalId,
      hospitalName: hospitalName || '',
      status: 'submitted', // submitted, under_review, approved, rejected, pending_documents
      priority: applicationData.priority || 'normal', // normal, high, urgent
      emergency: applicationData.emergency || false,
      submittedAt: new Date(),
      updatedAt: new Date(),
      // Store payment / disbursal preference (pdc | loan | '')
      paymentPreference: applicationData.paymentPreference || '',

      // Patient Information
      patient: {
        name: applicationData.patientName,
        age: parseInt(applicationData.patientAge),
        gender: applicationData.patientGender,
        address: applicationData.patientAddress,
        phone: applicationData.patientPhone,
        email: applicationData.patientEmail || '',

        // Patient KYC
        kyc: {
          aadharNumber: applicationData.patientAadhar,
          panNumber: applicationData.patientPan,
          addressProof: applicationData.patientAddressProof
        }
      },

      // Medical History - MOVED TO MEDICAL SECTION
      // (Removed standalone fields, now part of medical section)

      // Policy Holder Information
      policyHolder: {
        isSelf: applicationData.isSelf,
        name: applicationData.isSelf ? applicationData.patientName : applicationData.policyHolderName,
        age: applicationData.isSelf ? parseInt(applicationData.patientAge) : parseInt(applicationData.policyHolderAge || 0),
        gender: applicationData.isSelf ? applicationData.patientGender : applicationData.policyHolderGender,
        address: applicationData.isSelf ? applicationData.patientAddress : applicationData.policyHolderAddress,
        phone: applicationData.isSelf ? applicationData.patientPhone : applicationData.policyHolderPhone,
        email: applicationData.isSelf ? applicationData.patientEmail : (applicationData.policyHolderEmail || ''),
        // New fields: persist Policy Holder DOB and Alternate Mobile
        dob: applicationData.policyHolderDob || '',
        alternatePhone: applicationData.policyHolderAlternatePhone || '',
        relationshipWithPatient: applicationData.relationshipWithPatient || 'Self',

        // Policy Holder KYC
        kyc: {
          aadharNumber: applicationData.isSelf ? applicationData.patientAadhar : applicationData.policyHolderAadhar,
          panNumber: applicationData.isSelf ? applicationData.patientPan : applicationData.policyHolderPan,
          addressProof: applicationData.isSelf ? applicationData.patientAddressProof : applicationData.policyHolderAddressProof
        },

        // Nominee Information
        nominee: {
          name: applicationData.nomineeName,
          relationshipWithNominee: applicationData.relationshipWithNominee,
          contact: applicationData.nomineeContact
        }
      },

      // Policy Information - MOVED BEFORE MEDICAL IN FORM
      policy: {
        type: applicationData.policyType, // retail, corporate
        number: applicationData.policyNumber,
        startDate: applicationData.policyStartDate,
        endDate: applicationData.policyEndDate,
        insuranceCompany: applicationData.insuranceCompany,
        tpaName: applicationData.tpaName || ''
        // REMOVED: coverageAmount as requested
      },

      // Employment Information
      employment: {
        status: applicationData.employmentStatus || '',
        workingCompanyName: applicationData.workingCompanyName || '',
        monthlyIncome: parseFloat(applicationData.monthlyIncome) || 0,
        designation: applicationData.designation || '',
        natureOfWork: applicationData.natureOfWork || ''
      },

      // Lead Generator Information
      leadGenerator: applicationData.leadGenerator ? {
        id: applicationData.leadGenerator.id || "",
        entityName: applicationData.leadGenerator.entityName || "",
        city: applicationData.leadGenerator.city || "",
        state: applicationData.leadGenerator.state || "",
        servicesRequired: applicationData.leadGenerator.servicesRequired || [],
        cinNumber: applicationData.leadGenerator.cinNumber || "",
        leadGeneratorId: applicationData.leadGenerator.leadGeneratorId || applicationData.leadGenerator.id || "",
        assignedAt: applicationData.leadGenerator.assignedAt || new Date().toISOString(),
      } : null,

      // Medical Information - UPDATED
      medical: {
        // Updated to handle new medical history fields
        medicalHistory: {
          description: applicationData.medicalHistory || '',
          diagnosis: applicationData.medicalDiagnosis || '',
          duration: applicationData.medicalDuration || ''
        },
        surgicalHistory: {
          description: applicationData.surgicalHistory || '',
          diagnosis: applicationData.surgicalDiagnosis || '',
          duration: applicationData.surgicalDuration || '',
          procedure: applicationData.surgicalProcedure || ''
        },
        hasMedicalHistory: applicationData.hasMedicalHistory || false,
        medicalHistoryDocsCount: applicationData.medicalHistoryDocsCount || 0,

        // Treatment information
        treatmentType: applicationData.treatmentType || 'medical', // medical, surgical
        doctor: {
          name: applicationData.doctorName || '',
          designation: applicationData.doctorDesignation || ''
        },
        admissionDate: applicationData.admissionDate || '',
        dischargeDate: applicationData.dischargeDate || null,
        treatments: applicationData.treatments || [], // Array of diagnosis/treatment combinations
        estimatedCost: parseFloat(applicationData.estimatedCost) || 0
      },

      // Additional Information - MOVED TO DIAGNOSIS SECTION
      treatmentPlanned: applicationData.treatmentPlanned || 'planned', // emergency or planned
      remarks: applicationData.remarks || '',

      // Documents
      documents: documentUrls,

      // Workflow tracking
      workflow: {
        medicalVerification: {
          status: 'pending', // pending, approved, rejected
          verifiedBy: null,
          verifiedAt: null,
          comments: ''
        },
        policyVerification: {
          status: 'pending',
          verifiedBy: null,
          verifiedAt: null,
          comments: ''
        },
        kycVerification: {
          status: 'pending',
          verifiedBy: null,
          verifiedAt: null,
          comments: ''
        },
        // NEW: Financial verification automatically approved when paymentPreference is PDC
        financialVerification: applicationData.paymentPreference === 'pdc'
          ? {
            status: 'approved',
            verifiedBy: 'system_auto',
            verifiedAt: new Date(),
            comments: 'Auto-approved (PDC route selected at submission)'
          }
          : {
            status: 'pending', // will transition via FinancialReview workflow
            verifiedBy: null,
            verifiedAt: null,
            comments: ''
          }
      },
      
      // Bucket filters for efficient querying
      bucketFilter: {
        doctor: 'new',
        finance: null, // Not in finance pipeline yet
        policy: 'pending',
        hospital: 'submitted',
        admin: 'all',
        updatedAt: new Date()
      }
    };

    // Recursively clean the object to remove any undefined values
    const cleanObjectForFirestore = (obj) => {
      if (obj === null || obj === undefined) {
        return null;
      }

      if (Array.isArray(obj)) {
        return obj.map(item => cleanObjectForFirestore(item)).filter(item => item !== undefined);
      }

      if (typeof obj === 'object' && obj.constructor === Object) {
        const cleaned = {};
        for (const [key, value] of Object.entries(obj)) {
          const cleanedValue = cleanObjectForFirestore(value);
          if (cleanedValue !== undefined) {
            cleaned[key] = cleanedValue;
          }
        }
        return cleaned;
      }

      return obj;
    };

    const cleanedApplicationRecord = cleanObjectForFirestore(applicationRecord);

    // If auto-approved financialVerification (PDC), add a statusHistory-like entry for traceability
    if (applicationData.paymentPreference === 'pdc') {
      cleanedApplicationRecord.statusHistory = {
        ...(cleanedApplicationRecord.statusHistory || {}),
        financial_auto_approved: {
          updatedBy: 'system_auto',
          updatedAt: new Date(),
          comments: 'Financial verification auto-approved on submission (PDC route)'
        }
      };
    }

    // Debug logging for lead generator data
    console.log("Lead generator data before cleaning:", applicationRecord.leadGenerator);
    console.log("Lead generator data after cleaning:", cleanedApplicationRecord.leadGenerator);
    console.log("Employment data after cleaning:", cleanedApplicationRecord.employment);

    // Save to Firestore
    const docRef = await addDoc(collection(db, 'patientApplications'), cleanedApplicationRecord);

    // Fire-and-forget notification email (non-blocking)
    (async () => {
      try {
        const patientName = cleanedApplicationRecord.patient?.name || 'Patient';
        const hospital = cleanedApplicationRecord.hospitalName || 'Hospital';
        const appId = cleanedApplicationRecord.applicationId;
        const caseId = cleanedApplicationRecord.caseId;
        const subject = `New Application Created: ${patientName} (${appId})`;
        
        // Generate patient links for each recipient type
        const adminLink = getPatientApplicationLinkHtml(appId, patientName, 'admin@samriddhi.health', 'admin', `View Patient Application for ${patientName} (${appId})`);
        const doctorLink = getPatientApplicationLinkHtml(appId, patientName, 'doctors@samhealth.co.in', 'doctor', `View Patient Application for ${patientName} (${appId})`);
        const financeLink = getPatientApplicationLinkHtml(appId, patientName, 'cssg@samhealth.co.in', 'finance', `View Patient Application for ${patientName} (${appId})`);
        const supportLink = getPatientApplicationLinkHtml(appId, patientName, 'support@samhealth.co.in', 'admin', `View Patient Application for ${patientName} (${appId})`);
        
        const baseHtml = `<!DOCTYPE html><html><body style="font-family:Arial,Helvetica,sans-serif;line-height:1.5;color:#1e293b;">\n<h2 style="color:#0f766e;margin:0 0 12px;">New Patient Application Submitted</h2>\n<p style="margin:0 0 8px;">A new application has been created by <strong>${hospital}</strong>.</p>\n<table style="border-collapse:collapse;margin:12px 0;font-size:14px;">\n<tr><td style="padding:4px 8px;font-weight:600;">Patient</td><td style="padding:4px 8px;">${patientName}</td></tr>\n<tr><td style="padding:4px 8px;font-weight:600;">Application ID</td><td style="padding:4px 8px;">${appId}</td></tr>\n<tr><td style="padding:4px 8px;font-weight:600;">Case ID</td><td style="padding:4px 8px;">${caseId}</td></tr>\n<tr><td style="padding:4px 8px;font-weight:600;">Submitted At</td><td style="padding:4px 8px;">${new Date().toLocaleString()}</td></tr>\n<tr><td style="padding:4px 8px;font-weight:600;">Priority</td><td style="padding:4px 8px;">${cleanedApplicationRecord.priority}</td></tr>\n<tr><td style="padding:4px 8px;font-weight:600;">Emergency</td><td style="padding:4px 8px;">${cleanedApplicationRecord.emergency ? 'Yes' : 'No'}</td></tr>\n</table>\n<p style="margin:16px 0 0;">Please log in to the portal to review and proceed with the next steps.</p>`;
        const footerHtml = `\n<p style="font-size:12px;color:#64748b;margin-top:24px;">This is an automated notification. Do not reply.</p>\n</body></html>`;
        
        await sendEmail({ to: 'support@samhealth.co.in', subject, html: baseHtml + supportLink + footerHtml });
        await sendEmail({ to: 'admin@samriddhi.health', subject, html: baseHtml + adminLink + footerHtml });
        await sendEmail({ to: 'doctors@samhealth.co.in', subject, html: baseHtml + doctorLink + footerHtml });
        await sendEmail({ to: 'cssg@samhealth.co.in', subject, html: baseHtml + financeLink + footerHtml });

        // Send push notifications
        await sendApplicationCreatedPushNotification(cleanedApplicationRecord);

      } catch (e) {
        console.warn('Post-create email failed (non-blocking):', e?.message || e);
      }
    })();

    return {
      success: true,
      applicationId: cleanedApplicationRecord.applicationId,
      caseId: cleanedApplicationRecord.caseId,
      docId: docRef.id,
      message: 'Application submitted successfully!'
    };

  } catch (error) {
    console.error('Error creating application:', error);
    return {
      success: false,
      error: error.message || 'Failed to create application'
    };
  }
};
// Upload application document to Firebase Storage
export const uploadApplicationDocument = async (file, docType, patientName, hospitalId) => {
  try {
    // Guard: file must be a real File or Blob, not an array or plain object
    if (!file || (!(file instanceof File) && !(file instanceof Blob))) {
      throw new Error(`Invalid file object for ${docType}: expected File or Blob but received ${typeof file}`);
    }

    const timestamp = Date.now();
    // Add a random token to guarantee filename uniqueness and avoid Firebase's
    // ifGenerationMatch:0 returning 412 if a same-named object already exists
    const uniqueToken = Math.random().toString(36).slice(2, 8);
    const safeName = file.name || 'file';
    const fileName = `${docType}_${timestamp}_${uniqueToken}_${safeName}`;
    const storagePath = `applications/${hospitalId}/${patientName.replace(/\s+/g, '_')}/documents/${fileName}`;

    const storageRef = ref(storage, storagePath);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);

    return downloadURL;
  } catch (error) {
    console.error('Error uploading document:', error);
    throw new Error(`Failed to upload ${docType}: ${error.message}`);
  }
};

// Get applications for a specific hospital with pagination and filters
export const getHospitalApplications = async (hospitalId, filters = {}, pageSize = 10, lastDoc = null) => {
  try {
    let q = collection(db, 'patientApplications');

    // Filter by hospital
    q = query(q, where('hospitalId', '==', hospitalId));

    // Apply filters
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }

    if (filters.emergency !== undefined) {
      q = query(q, where('emergency', '==', filters.emergency));
    }

    if (filters.priority) {
      q = query(q, where('priority', '==', filters.priority));
    }

    if (filters.treatmentType) {
      q = query(q, where('medical.treatmentType', '==', filters.treatmentType));
    }

    // Order by submission date (newest first)
    q = query(q, orderBy('submittedAt', 'desc'));

    // Pagination
    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }

    q = query(q, limit(pageSize));

    const querySnapshot = await getDocs(q);
    const applications = [];
    let lastDocument = null;

    querySnapshot.forEach((doc) => {
      applications.push({
        id: doc.id,
        ...doc.data()
      });
      lastDocument = doc;
    });

    return {
      success: true,
      applications,
      lastDocument,
      hasMore: querySnapshot.docs.length === pageSize
    };
  } catch (error) {
    console.error('Error fetching applications:', error);
    return {
      success: false,
      error: error.message,
      applications: []
    };
  }
};

// Get single application by ID
export const getApplication = async (applicationId) => {
  try {
    const q = query(
      collection(db, 'patientApplications'),
      where('applicationId', '==', applicationId)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return {
        success: false,
        error: 'Application not found'
      };
    }

    const doc = querySnapshot.docs[0];
    return {
      success: true,
      application: {
        id: doc.id,
        ...doc.data()
      }
    };
  } catch (error) {
    console.error('Error fetching application:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Alias for getApplication to match component usage
export const getPatientApplication = getApplication;

// Update application status
export const updateApplicationStatus = async (applicationId, status, updatedBy, comments = '') => {
  try {
    const appResult = await getApplication(applicationId);
    if (!appResult.success) {
      throw new Error('Application not found');
    }

    const applicationRef = doc(db, 'patientApplications', appResult.application.id);
    await updateDoc(applicationRef, {
      status: status,
      updatedAt: new Date(),
      [`statusHistory.${status}`]: {
        updatedBy: updatedBy,
        updatedAt: new Date(),
        comments: comments
      }
    });

    return {
      success: true,
      message: 'Application status updated successfully'
    };
  } catch (error) {
    console.error('Error updating application status:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Update workflow verification status
export const updateWorkflowStatus = async (applicationId, workflowType, status, verifiedBy, comments = '') => {
  try {
    const appResult = await getApplication(applicationId);
    if (!appResult.success) {
      throw new Error('Application not found');
    }

    const applicationRef = doc(db, 'patientApplications', appResult.application.id);
    await updateDoc(applicationRef, {
      [`workflow.${workflowType}.status`]: status,
      [`workflow.${workflowType}.verifiedBy`]: verifiedBy,
      [`workflow.${workflowType}.verifiedAt`]: new Date(),
      [`workflow.${workflowType}.comments`]: comments,
      updatedAt: new Date()
    });

    return {
      success: true,
      message: `${workflowType} verification updated successfully`
    };
  } catch (error) {
    console.error('Error updating workflow status:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Search applications
export const searchApplications = async (hospitalId, searchTerm, filters = {}) => {
  try {
    let q = collection(db, 'patientApplications');
    q = query(q, where('hospitalId', '==', hospitalId));

    // Apply additional filters
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        q = query(q, where(key, '==', filters[key]));
      }
    });

    q = query(q, orderBy('submittedAt', 'desc'));

    const querySnapshot = await getDocs(q);
    const applications = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Client-side search filtering
      const searchableText = `
        ${data.applicationId}
        ${data.patient.name}
        ${data.medical.diagnosis}
        ${data.policy.number}
        ${data.medical.doctor.name}
      `.toLowerCase();

      if (searchableText.includes(searchTerm.toLowerCase())) {
        applications.push({
          id: doc.id,
          ...data
        });
      }
    });

    return {
      success: true,
      applications
    };
  } catch (error) {
    console.error('Error searching applications:', error);
    return {
      success: false,
      error: error.message,
      applications: []
    };
  }
};

// Get application statistics for dashboard
export const getApplicationStats = async (hospitalId) => {
  try {
    const q = query(
      collection(db, 'patientApplications'),
      where('hospitalId', '==', hospitalId)
    );

    const querySnapshot = await getDocs(q);
    const stats = {
      total: 0,
      submitted: 0,
      underReview: 0,
      approved: 0,
      rejected: 0,
      emergency: 0,
      pendingDocuments: 0
    };

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      stats.total++;

      switch (data.status) {
        case 'submitted':
          stats.submitted++;
          break;
        case 'under_review':
          stats.underReview++;
          break;
        case 'approved':
          stats.approved++;
          break;
        case 'rejected':
          stats.rejected++;
          break;
        case 'pending_documents':
          stats.pendingDocuments++;
          break;
      }

      if (data.emergency) {
        stats.emergency++;
      }
    });

    return {
      success: true,
      stats
    };
  } catch (error) {
    console.error('Error fetching application stats:', error);
    return {
      success: false,
      error: error.message,
      stats: {
        total: 0,
        submitted: 0,
        underReview: 0,
        approved: 0,
        rejected: 0,
        emergency: 0,
        pendingDocuments: 0
      }
    };
  }
};

// Delete application document
export const deleteApplicationDocument = async (documentUrl) => {
  try {
    const storageRef = ref(storage, documentUrl);
    await deleteObject(storageRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting document:', error);
    return { success: false, error: error.message };
  }
};

// ===== DISCUSSION SYSTEM FUNCTIONS =====

// Get application discussions
export const getApplicationDiscussions = async (applicationId) => {
  try {
    const q = query(
      collection(db, 'discussions'),
      where('applicationId', '==', applicationId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const discussions = [];

    querySnapshot.forEach((doc) => {
      discussions.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return {
      success: true,
      discussions
    };
  } catch (error) {
    console.error('Error fetching discussions:', error);
    return {
      success: false,
      error: error.message,
      discussions: []
    };
  }
};

// Send discussion message
export const sendDiscussionMessage = async (applicationId, messageData) => {
  try {
    let discussionId = messageData.discussionId;

    // If no discussionId provided, create a new discussion
    if (!discussionId) {
      const discussionDoc = {
        applicationId,
        subject: messageData.subject || 'General Discussion',
        description: messageData.description || 'Application discussion',
        participants: [messageData.sender],
        createdAt: new Date(),
        updatedAt: new Date(),
        messages: []
      };

      const discussionRef = await addDoc(collection(db, 'discussions'), discussionDoc);
      discussionId = discussionRef.id;
    }

    // Prepare message object
    const message = {
      id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
      message: messageData.message,
      sender: messageData.sender,
      senderName: messageData.senderName,
      senderRole: messageData.senderRole,
      timestamp: messageData.timestamp || new Date(),
      readBy: messageData.readBy || [],
      replyTo: messageData.replyTo || null,
      attachments: messageData.attachments || []
    };

    // Add message to discussion
    const discussionDoc = doc(db, 'discussions', discussionId);

    if (messageData.replyTo) {
      // This is a reply to an existing message
      const discussionSnapshot = await getDoc(discussionDoc);
      const discussion = discussionSnapshot.data();

      const updatedMessages = discussion.messages.map(msg => {
        if (msg.id === messageData.replyTo) {
          return {
            ...msg,
            replies: [...(msg.replies || []), message]
          };
        }
        return msg;
      });

      await updateDoc(discussionDoc, {
        messages: updatedMessages,
        updatedAt: new Date(),
        participants: arrayUnion(messageData.sender)
      });
    } else {
      // This is a new top-level message
      await updateDoc(discussionDoc, {
        messages: arrayUnion(message),
        updatedAt: new Date(),
        participants: arrayUnion(messageData.sender)
      });
    }

    return {
      success: true,
      messageId: message.id,
      discussionId
    };
  } catch (error) {
    console.error('Error sending message:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Mark messages as read
export const markMessagesAsRead = async (applicationId, userId) => {
  try {
    const q = query(
      collection(db, 'discussions'),
      where('applicationId', '==', applicationId)
    );

    const querySnapshot = await getDocs(q);

    const updatePromises = [];
    querySnapshot.forEach((docSnapshot) => {
      const discussion = docSnapshot.data();

      const updatedMessages = discussion.messages.map(message => ({
        ...message,
        readBy: [...new Set([...(message.readBy || []), userId])],
        replies: (message.replies || []).map(reply => ({
          ...reply,
          readBy: [...new Set([...(reply.readBy || []), userId])]
        }))
      }));

      updatePromises.push(
        updateDoc(doc(db, 'discussions', docSnapshot.id), {
          messages: updatedMessages
        })
      );
    });

    await Promise.all(updatePromises);

    return {
      success: true,
      message: 'Messages marked as read'
    };
  } catch (error) {
    console.error('Error marking messages as read:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Upload discussion document
export const uploadDiscussionDocument = async (applicationId, file, metadata) => {
  try {
    const uniqueToken = Math.random().toString(36).slice(2, 8);
    const fileName = `discussion_${Date.now()}_${uniqueToken}_${file.name}`;
    const storageRef = ref(storage, `discussions/${applicationId}/${fileName}`);

    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);

    const documentData = {
      url: downloadURL,
      fileName: file.name,
      size: file.size,
      uploadedAt: new Date(),
      uploadedBy: metadata.uploadedBy,
      uploaderName: metadata.uploaderName,
      discussionId: metadata.discussionId,
      messageId: metadata.messageId
    };

    // If this is part of a message, add to message attachments
    if (metadata.discussionId && metadata.messageId) {
      const discussionDoc = doc(db, 'discussions', metadata.discussionId);
      const discussionSnapshot = await getDoc(discussionDoc);
      const discussion = discussionSnapshot.data();

      const updatedMessages = discussion.messages.map(msg => {
        if (msg.id === metadata.messageId) {
          return {
            ...msg,
            attachments: [...(msg.attachments || []), documentData]
          };
        }
        return msg;
      });

      await updateDoc(discussionDoc, {
        messages: updatedMessages
      });
    }

    return {
      success: true,
      document: documentData
    };
  } catch (error) {
    console.error('Error uploading discussion document:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// ===== SANCTIONS & LETTERS SYSTEM =====

// Get application sanctions/letters
export const getApplicationSanctions = async (applicationId) => {
  try {
    const q = query(
      collection(db, 'sanctions'),
      where('applicationId', '==', applicationId),
      orderBy('generatedAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const sanctions = [];

    querySnapshot.forEach((doc) => {
      sanctions.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return {
      success: true,
      sanctions
    };
  } catch (error) {
    console.error('Error fetching sanctions:', error);
    return {
      success: false,
      error: error.message,
      sanctions: []
    };
  }
};

// Generate letter (KYP, approval, disbursal, closure)
// FAL (approval letter) generation requires NO pending queries from ANY team (global check)
export const generateLetter = async (applicationId, letterType, options = {}) => {
  try {
    const { publishToWorkflowQuickLink = true, dischargeItemsOverride = null } = options;
    const result = await getApplication(applicationId);
    if (!result.success) {
      return result;
    }

    const application = result.application;
    
    // CRITICAL: FAL (approval letter) generation requires NO pending queries from ANY team
    if (letterType === 'approval') {
      const { checkApplicationDisputeStatus } = await import('./disputeService');
      const globalQueryCheck = await checkApplicationDisputeStatus(applicationId);
      
      if (!globalQueryCheck.success) {
        return { 
          success: false, 
          error: "Error checking query status before FAL generation" 
        };
      }
      
      if (globalQueryCheck.hasActiveDisputes) {
        const teams = [...new Set(globalQueryCheck.activeDisputes.map(d => d.raisedBy?.department || 'Unknown'))];
        return {
          success: false,
          error: `Cannot generate Final Approval Letter (FAL). There are ${globalQueryCheck.activeDisputes.length} unresolved ${globalQueryCheck.activeDisputes.length === 1 ? 'query' : 'queries'} from ${teams.join(', ')} team(s). All queries must be resolved before FAL generation.`,
          hasUnresolvedQueries: true,
          unresolvedQueries: globalQueryCheck.activeDisputes,
          queryCount: globalQueryCheck.activeDisputes.length
        };
      }
    }

  // Generate letter content based on type
  const letterContent = generateLetterContent(application, letterType, { dischargeItemsOverride });

    const fileName = `${letterType}_letter_${application.applicationId}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}.pdf`;
    const storageRef = ref(storage, `letters/${applicationId}/${fileName}`);

    // Build a valid PDF using jsPDF so it opens in viewers correctly
    const docPdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = docPdf.internal.pageSize.getWidth();
    const margin = 20; // mm

    const teal = { r: 0, g: 102, b: 153 };

    const drawHeader = (title, subtitle = '') => {
      docPdf.setFont('helvetica', 'normal');
      docPdf.setFontSize(18);
      docPdf.setTextColor(teal.r, teal.g, teal.b);
      docPdf.text(title, pageWidth / 2, margin + 5, { align: 'center' });
      if (subtitle) {
        docPdf.setFontSize(12);
        docPdf.text(subtitle, pageWidth / 2, margin + 12, { align: 'center' });
      }
      docPdf.setDrawColor(teal.r, teal.g, teal.b);
      docPdf.line(margin, margin + 18, pageWidth - margin, margin + 18);
      docPdf.setTextColor(0, 0, 0);
      docPdf.setFont('helvetica', 'normal');
      docPdf.setFontSize(11);
    };

    const addWrappedText = (text, x, y, maxWidth, lineHeight = 5) => {
      const split = docPdf.splitTextToSize(text, maxWidth);
      split.forEach((line, idx) => docPdf.text(line, x, y + idx * lineHeight));
      return y + split.length * lineHeight;
    };

    if (letterType === 'approval') {
      // Header with KYP-like styling
      drawHeader('FINAL APPROVAL LETTER', 'DISCHARGE SETTLEMENT CONFIRMATION');
      let y = margin + 28;
      const sectionGap = 8;
      const maxWidth = pageWidth - margin * 2;

      const currentDate = new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });
      // Document details
      docPdf.setFontSize(10);
      docPdf.setTextColor(0, 0, 0);
      docPdf.text(`Date: ${currentDate}`, margin, y);
      docPdf.text(`Application ID: ${application.applicationId}`, margin, y + 5);
      docPdf.text(`Reference No: APR-${application.applicationId}-${Date.now()}`, margin, y + 10);
      y += 20;

      // Address block
      y = addWrappedText(`To\nThe Authorized Representative\n${application.hospitalName || '-'}`, margin, y, maxWidth) + sectionGap;

      // Intro
      docPdf.setFontSize(10);
      y = addWrappedText('This is to certify that the claim has been reviewed post discharge and the final settlement amount has been approved as per the details below.', margin, y, maxWidth) + 4;

      // Section: PATIENT & POLICY DETAILS
      docPdf.setFontSize(13);
      docPdf.setTextColor(teal.r, teal.g, teal.b);
      docPdf.text('PATIENT & POLICY DETAILS', margin, y);
      y += 7;
      docPdf.setTextColor(0, 0, 0);
      docPdf.setFontSize(10);
      const pDetails = [
        `• Patient Name: ${application.patient?.name || '-'}`,
        `• Age: ${application.patient?.age ?? '-'} years`,
        `• Gender: ${application.patient?.gender || '-'}`,
        `• Primary Diagnosis: ${application.medical?.diagnosis || '-'}`,
        `• Policy Number: ${application.policy?.number || '-'}`,
        `• Insurance Company: ${application.policy?.insuranceCompany || '-'}`
      ];
      pDetails.forEach((line) => { docPdf.text(line, margin, y); y += 5; });
      y += 3;

      // Section: HOSPITAL INFORMATION
      docPdf.setFontSize(13);
      docPdf.setTextColor(teal.r, teal.g, teal.b);
      docPdf.text('HOSPITAL INFORMATION', margin, y);
      y += 7;
      docPdf.setTextColor(0, 0, 0);
      docPdf.setFontSize(10);
      const hDetails = [
        `• Hospital Name: ${application.hospitalName || '-'}`,
        `• Discharge Date: ${(() => { const d = application.medical?.dischargeDate; const dd = d?.toDate ? d.toDate() : (d ? new Date(d) : null); return dd ? dd.toLocaleDateString('en-IN') : '-' })()}`
      ];
      hDetails.forEach((line) => { docPdf.text(line, margin, y); y += 5; });
      y += 3;

      // Section: DISCHARGE REVIEW SUMMARY
      // Robust items fallback: override -> saved itemsBreakdown -> other sources
      const dischargeItems = (
        dischargeItemsOverride ||
        application.workflow?.dischargeSettlement?.itemsBreakdown ||
        application.workflow?.medicalVerification?.dischargeReview?.items ||
        application.workflow?.dischargeSettlement?.falComputation?.items ||
        application.workflow?.dischargeSettlement?.items ||
        application.dischargeSummary?.items ||
        []
      );
      const itemsCount = dischargeItems.length || 0;
      
      // Determine approved amount from multiple reliable sources
      // Priority: medicalVerification.finalApprovedAmount -> dischargeSettlement.finalSettlementAmount -> falComputation.totals.netPayable/approved
      const docApproved = (
        application.workflow?.medicalVerification?.finalApprovedAmount ??
        application.workflow?.dischargeSettlement?.finalSettlementAmount ??
        application.workflow?.dischargeSettlement?.falComputation?.totals?.netPayable ??
        application.workflow?.dischargeSettlement?.falComputation?.totals?.approved ??
        0
      );
      
      // Validation: Only require that there are discharge items
      if (!itemsCount || itemsCount === 0) {
        throw new Error('FAL cannot be generated without discharge items. Please add and review discharge items first.');
      }
      
      docPdf.setFontSize(13);
      docPdf.setTextColor(teal.r, teal.g, teal.b);
      docPdf.text('DISCHARGE REVIEW SUMMARY', margin, y);
      y += 7;
      docPdf.setTextColor(0, 0, 0);
      docPdf.setFontSize(10);
      [
        `• Items Verified: ${itemsCount}`,
        `• Doctor Approved Amount: ₹${(docApproved || 0).toLocaleString('en-IN')}`
      ].forEach((line) => { docPdf.text(line, margin, y); y += 5; });
      y += 3;

      // Highlighted: FINAL SETTLEMENT SUMMARY WITH COPAY & SUBLIMIT DETAILS
      const settlement = application.workflow?.dischargeSettlement || {};
      const approved = docApproved || 0;
      const deductions = Array.isArray(settlement.deductions) ? settlement.deductions : [];
      const adjustments = Array.isArray(settlement.adjustments) ? settlement.adjustments : [];
      const totalDeductions = deductions.reduce((a, d) => a + (d?.percentage ? ((approved * (parseFloat(d.amount) || 0)) / 100) : (parseFloat(d.amount) || 0)), 0);
      const totalAdjustments = adjustments.reduce((a, x) => a + (parseFloat(x.amount) || 0), 0);
      const finalSettlement = settlement.finalSettlementAmount ?? approved;

      // Get copay and sublimit information from policy verification
      const policyVerification = application.workflow?.policyVerification || {};
      const coPayStructure = policyVerification.coPayStructure || {};
      const deductiblesAndSublimits = policyVerification.deductiblesAndSublimits || {};
      
      // Calculate copay amount from FAL computation if available
      const falComputation = application.workflow?.dischargeSettlement?.falComputation;
      const copayAmount = falComputation?.totals?.copay || 0;
      const sublimitAmount = falComputation?.totals?.sublimits || 0;
      
      // Fallback copay calculation
      let fallbackCopayAmount = 0;
      if (!copayAmount && coPayStructure.generalCoPayApplicable) {
        const copayPercent = parseFloat(coPayStructure.generalCoPayValue) || 0;
        if (copayPercent > 0) {
          fallbackCopayAmount = (approved * copayPercent) / 100;
        }
      }
      
      const displayCopayAmount = copayAmount || fallbackCopayAmount;

      docPdf.setFillColor(240, 249, 255); // light blue
      docPdf.rect(margin - 5, y - 4, pageWidth - margin * 2 + 10, 45, 'F');
      docPdf.setFontSize(13);
      docPdf.setTextColor(teal.r, teal.g, teal.b);
      docPdf.text('FINAL SETTLEMENT SUMMARY', margin, y);
      y += 7;
      docPdf.setTextColor(0, 0, 0);
      docPdf.setFontSize(11);
      docPdf.text(`Total Deductions: ₹${(totalDeductions || 0).toLocaleString('en-IN')}`, margin, y);
      docPdf.text(`Total Adjustments: ₹${(totalAdjustments || 0).toLocaleString('en-IN')}`, margin + 90, y);
      y += 6;
      
      // Add copay deduction line
      if (displayCopayAmount > 0) {
        docPdf.text(`Co-pay Deduction: ₹${displayCopayAmount.toLocaleString('en-IN')}`, margin, y);
        const copayDetails = coPayStructure.generalCoPayApplicable ? 
          ` (${coPayStructure.generalCoPayValue || 0}%)` : '';
        docPdf.setFontSize(9);
        docPdf.text(copayDetails, margin + 70, y);
        docPdf.setFontSize(11);
        y += 6;
      }
      
      // Add sublimit deduction line
      if (sublimitAmount > 0) {
        docPdf.text(`Sublimit Deduction: ₹${sublimitAmount.toLocaleString('en-IN')}`, margin, y);
        y += 6;
      }
      
      docPdf.setFont('helvetica', 'bold');
      docPdf.text(`FINAL APPROVED AMOUNT: ₹${(finalSettlement || 0).toLocaleString('en-IN')}`, margin, y);
      docPdf.setFont('helvetica', 'normal');
      y += 10;

      // Notes
      docPdf.setFontSize(13);
      docPdf.setTextColor(teal.r, teal.g, teal.b);
      docPdf.text('NOTES', margin, y);
      y += 6;
      docPdf.setTextColor(0, 0, 0);
      docPdf.setFontSize(10);
      y = addWrappedText((settlement.comments || 'No additional comments').trim(), margin, y, maxWidth) + 4;

      // Statement & sign-off
      y = addWrappedText('This letter is issued as the final approval confirmation and may be shared with the hospital billing team for discharge settlement. Disbursal will proceed as per standard process and timelines.', margin, y, maxWidth) + 8;
      docPdf.setFont('helvetica', 'bold');
      docPdf.text('For and on behalf of SamHealth', margin, y);
      docPdf.setFont('helvetica', 'normal');
      docPdf.text('Authorization Team', margin, y + 6);
    } else {
      // Generic fallback for other letters using plain content with wrapping
      drawHeader(getLetterTitle(letterType));
      addWrappedText(letterContent, margin, margin + 60, pageWidth - margin * 2);
    }

    const pdfBlob = docPdf.output('blob');
    const snapshot = await uploadBytes(storageRef, pdfBlob, { contentType: 'application/pdf' });
    const downloadURL = await getDownloadURL(snapshot.ref);

    // Save letter record
    const letterData = {
      applicationId,
      type: letterType,
      title: getLetterTitle(letterType),
      url: downloadURL,
      fileName,
      generatedAt: new Date(),
      generatedBy: 'System', // In real implementation, use actual user
      content: letterContent,
      hospitalAcknowledgement: {
        status: 'pending', // accepted | challenged | pending
        comment: '',
        by: null,
        byName: null,
        at: null
      }
    };

    const docRef = await addDoc(collection(db, 'sanctions'), letterData);

    // Update application with letter flag
    const applicationDoc = doc(db, 'patientApplications', application.id);
    await updateDoc(applicationDoc, {
      [`${letterType}LetterGenerated`]: true,
      // Persist url for quick access (esp. for hospital visibility for approval letter). Can be deferred for draft flow.
      ...(letterType === 'approval' && publishToWorkflowQuickLink ? { 'workflow.dischargeSettlement.finalApprovalLetterUrl': downloadURL } : {}),
      updatedAt: new Date()
    });

    // Update bucket filter when FAL is generated
    if (letterType === 'approval') {
      await updateRoleBucket(application.id, 'doctor', 'discharge_approved');
      await updateRoleBucket(application.id, 'finance', 'falGenerated');
    }

    // Fire-and-forget: Email when FAL is generated/published
    if (letterType === 'approval') {
      (async () => {
        try {
          const subject = `FAL Generated: ${application.applicationId}`;
          const patientName = application.patient?.name || 'Patient';
          const patientLink = getPatientApplicationLinkHtml(
            application.applicationId,
            patientName,
            'cssg@samhealth.co.in',
            'finance',
            `View Patient Application for ${patientName} (${application.applicationId})`
          );
          const html = `<!DOCTYPE html><html><body style=\"font-family:Arial,Helvetica,sans-serif;line-height:1.5;color:#0f172a;\">\n<h2 style=\"color:#0f766e;margin:0 0 12px;\">Final Approval Letter Generated</h2>\n<p style=\"margin:0 0 8px;\">A Final Approval Letter has been generated for application <strong>${application.applicationId}</strong>.</p>\n${patientLink}\n<p style=\"margin:8px 0 0;font-size:12px;color:#64748b;\">This is an automated notification.</p>\n</body></html>`;
          await sendEmail({ to: 'cssg@samhealth.co.in', subject, html });
        } catch (e) {
          console.warn('Non-blocking email failed (generateLetter:FAL):', e?.message || e);
        }
      })();
    }

    return {
      success: true,
      letterUrl: downloadURL,
      fileName,
      letterId: docRef.id
    };
  } catch (error) {
    console.error('Error generating letter:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Helper function to generate letter content
const generateLetterContent = (application, letterType, options = {}) => {
  const { dischargeItemsOverride = null } = options;
  const currentDate = new Date().toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  switch (letterType) {
    case 'kyp': {
      const fullName = getFullPatientName(application);
      return `
KNOW YOUR PATIENT (KYP) LETTER

Date: ${currentDate}
Application ID: ${application.applicationId}

To Whom It May Concern,

This is to certify that we have reviewed and verified the medical documents submitted for the patient ${fullName} (Age: ${application.patient?.age}, Gender: ${application.patient?.gender}).

Patient Details:
- Name: ${fullName}
- Age: ${application.patient?.age} years
- Gender: ${application.patient?.gender}
- Diagnosis: ${application.medical?.diagnosis}
- Treatment Type: ${application.medical?.treatmentType}
- Consulting Doctor: ${application.medical?.doctor?.name}

Medical Verification Status: ${application.workflow?.medicalVerification?.status}
Verified By: Medical Team
Verification Date: ${currentDate}

This letter serves as official confirmation of patient identity and medical case verification.

Best Regards,
SamHealth Medical Team
  `;
    }

    case 'approval': {
      const settlement = application.workflow?.dischargeSettlement || {};
      
      // Determine approved amount from multiple reliable sources
      // Priority: medicalVerification.finalApprovedAmount -> dischargeSettlement.finalSettlementAmount -> falComputation.totals.netPayable/approved
      const approved = (
        application.workflow?.medicalVerification?.finalApprovedAmount ??
        application.workflow?.dischargeSettlement?.finalSettlementAmount ??
        application.workflow?.dischargeSettlement?.falComputation?.totals?.netPayable ??
        application.workflow?.dischargeSettlement?.falComputation?.totals?.approved ??
        0
      );
      // Robust items fallback: override -> saved itemsBreakdown -> other sources
      const dischargeItems = (
        dischargeItemsOverride ||
        application.workflow?.dischargeSettlement?.itemsBreakdown ||
        application.workflow?.medicalVerification?.dischargeReview?.items ||
        application.workflow?.dischargeSettlement?.falComputation?.items ||
        application.workflow?.dischargeSettlement?.items ||
        application.dischargeSummary?.items ||
        []
      );
      
      // Validation: Only require that there are discharge items
      if (!dischargeItems.length) {
        throw new Error('FAL cannot be generated without discharge items. Please add and review discharge items first.');
      }
      
      const finalSettlement = settlement.finalSettlementAmount ?? approved;
      const deductions = Array.isArray(settlement.deductions) ? settlement.deductions : [];
      const adjustments = Array.isArray(settlement.adjustments) ? settlement.adjustments : [];
      const formatINR = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;

      // Get copay and sublimit information
      const policyVerification = application.workflow?.policyVerification || {};
      const coPayStructure = policyVerification.coPayStructure || {};
      const deductiblesAndSublimits = policyVerification.deductiblesAndSublimits || {};
      
      // Calculate copay amount from FAL computation if available
      const falComputation = application.workflow?.dischargeSettlement?.falComputation;
      const copayAmount = falComputation?.totals?.copay || 0;
      const sublimitAmount = falComputation?.totals?.sublimits || 0;
      
      // Fallback copay calculation
      let fallbackCopayAmount = 0;
      if (!copayAmount && coPayStructure.generalCoPayApplicable) {
        const copayPercent = parseFloat(coPayStructure.generalCoPayValue) || 0;
        if (copayPercent > 0) {
          fallbackCopayAmount = (approved * copayPercent) / 100;
        }
      }
      
      const displayCopayAmount = copayAmount || fallbackCopayAmount;

      // Build copay details string
      let copayDetails = '';
      if (displayCopayAmount > 0) {
        const copayLine = `- Co-pay Deduction: ${formatINR(displayCopayAmount)}`;
        const copayPercent = coPayStructure.generalCoPayApplicable ? 
          ` (${coPayStructure.generalCoPayValue || 0}% as per policy)` : '';
        copayDetails = copayLine + copayPercent;
      }
      
      // Build sublimit details string  
      let sublimitDetails = '';
      if (sublimitAmount > 0) {
        sublimitDetails = `- Sublimit Deduction: ${formatINR(sublimitAmount)}`;
      }

      return `
FINAL APPROVAL LETTER

Date: ${currentDate}
Application ID: ${application.applicationId}

To,
The Authorized Representative,
${application.hospitalName}

Subject: Final Approval of Claim and Settlement Confirmation

Dear Sir/Madam,

This is to confirm that the claim for the below-mentioned patient has been reviewed and finalized post discharge. The final settlement amount has been approved as per the details below.

Patient & Policy Details:
- Patient Name: ${application.patient?.name}
- Age/Gender: ${application.patient?.age || '-'} / ${application.patient?.gender || '-'}
- Diagnosis: ${application.medical?.diagnosis || '-'}
- Policy Number: ${application.policy?.number || '-'}
- Insurance Company: ${application.policy?.insuranceCompany || '-'}

Financial Summary:
- Doctor Approved Amount: ${formatINR(approved)}
- Total Deductions: ${formatINR(deductions.reduce((a, d) => a + (d.percentage ? ((approved * (parseFloat(d.amount) || 0)) / 100) : (parseFloat(d.amount) || 0)), 0))}
- Total Adjustments: ${formatINR(adjustments.reduce((a, x) => a + (parseFloat(x.amount) || 0), 0))}${copayDetails ? '\n' + copayDetails : ''}${sublimitDetails ? '\n' + sublimitDetails : ''}
- Final Settlement Amount (Approved): ${formatINR(finalSettlement)}

Notes:
${(settlement.comments || 'No additional comments').trim()}

This letter serves as the final approval confirmation and may be shared with the hospital billing desk for discharge settlement purposes. Disbursal shall proceed as per the standard process and timelines.

For and on behalf of SamHealth
Authorization Team
      `;
    }

    case 'disbursal':
      return `
DISBURSAL AUTHORIZATION LETTER

Date: ${currentDate}
Application ID: ${application.applicationId}

AUTHORIZATION FOR TREATMENT COST DISBURSAL

This letter authorizes the disbursal of treatment costs for the approved insurance claim.

Patient Details:
- Name: ${application.patient?.name}
- Policy Number: ${application.policy?.number}
- Approved Amount: ₹${application.medical?.estimatedCost?.toLocaleString('en-IN')}

Hospital Details:
- Hospital Name: ${application.hospitalName}
- Treatment Period: ${application.medical?.admissionDate} to ${application.medical?.dischargeDate || 'Ongoing'}

This authorization permits the hospital to proceed with cashless treatment for the patient as per the approved coverage amount.

Authorized By: Financial Team
Authorization Date: ${currentDate}

Best Regards,
SamHealth Finance Department
      `;

    case 'closure':
      return `
CASE CLOSURE LETTER

Date: ${currentDate}
Application ID: ${application.applicationId}

FINAL CASE CLOSURE AND SETTLEMENT

This letter confirms the successful closure of the insurance claim case.

Settlement Details:
- Patient Name: ${application.patient?.name}
- Policy Number: ${application.policy?.number}
- Final Settled Amount: ₹${application.medical?.actualCost?.toLocaleString('en-IN') || application.medical?.estimatedCost?.toLocaleString('en-IN')}
- Settlement Date: ${currentDate}

All obligations under this claim have been fulfilled. The case is hereby officially closed.

Thank you for choosing SamHealth for your insurance processing needs.

Best Regards,
SamHealth Settlement Team
      `;

    default:
      return `Letter content for ${letterType} not available.`;
  }
};

// Helper function to get letter title
const getLetterTitle = (letterType) => {
  switch (letterType) {
    case 'kyp':
      return 'Know Your Patient (KYP) Letter';
    case 'approval':
      return 'Final Approval Letter';
    case 'disbursal':
      return 'Disbursal Authorization Letter';
    case 'closure':
      return 'Case Closure Letter';
    default:
      return 'Official Letter';
  }
};

// ===== UTILITY FUNCTIONS =====

// Generate unique application ID
const generateApplicationId = async () => {
  try {
    const db = getFirestore();
    const counterRef = doc(db, 'counters', 'applicationId');
    
    // Use transaction to ensure atomicity
    const newId = await runTransaction(db, async (transaction) => {
      const counterDoc = await transaction.get(counterRef);
      
      let currentCount = 1;
      if (counterDoc.exists()) {
        currentCount = (counterDoc.data().count || 0) + 1;
      }
      
      // Update the counter
      transaction.set(counterRef, { count: currentCount, updatedAt: serverTimestamp() }, { merge: true });
      
      // Generate the application ID with padding (SAM00001, SAM00002, etc.)
      const paddedNumber = currentCount.toString().padStart(5, '0');
      return `SAM${paddedNumber}`;
    });
    
    return newId;
  } catch (error) {
    console.error('Error generating application ID:', error);
    // Fallback to timestamp-based ID if counter fails
    const timestamp = Date.now().toString().slice(-8);
    const random = Math.random().toString(36).substr(2, 4).toUpperCase();
    return `SAM${timestamp}${random}`;
  }
};

// Get unread messages count for application
export const getUnreadMessagesCount = async (applicationId, userId) => {
  try {
    const discussions = await getApplicationDiscussions(applicationId);
    if (!discussions.success) {
      return { success: false, count: 0 };
    }

    let unreadCount = 0;
    discussions.discussions.forEach(discussion => {
      discussion.messages?.forEach(message => {
        if (!message.readBy?.includes(userId)) {
          unreadCount++;
        }
        // Count unread replies
        message.replies?.forEach(reply => {
          if (!reply.readBy?.includes(userId)) {
            unreadCount++;
          }
        });
      });
    });

    return {
      success: true,
      count: unreadCount
    };
  } catch (error) {
    console.error('Error getting unread messages count:', error);
    return {
      success: false,
      count: 0
    };
  }
};

// Add application comment/note
export const addApplicationComment = async (applicationId, comment, commentBy) => {
  try {
    const result = await getApplication(applicationId);
    if (!result.success) {
      return result;
    }

    const application = result.application;
    const applicationRef = doc(db, 'patientApplications', application.id);

    const commentData = {
      id: `comment_${Date.now()}`,
      comment: comment,
      commentBy: commentBy,
      commentAt: new Date()
    };

    await updateDoc(applicationRef, {
      comments: arrayUnion(commentData),
      updatedAt: new Date()
    });

    return {
      success: true,
      message: 'Comment added successfully'
    };
  } catch (error) {
    console.error('Error adding comment:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Update application priority
export const updateApplicationPriority = async (applicationId, priority, updatedBy) => {
  try {
    const result = await getApplication(applicationId);
    if (!result.success) {
      return result;
    }

    const application = result.application;
    const applicationRef = doc(db, 'patientApplications', application.id);

    await updateDoc(applicationRef, {
      priority: priority,
      updatedAt: new Date(),
      priorityUpdatedBy: updatedBy,
      priorityUpdatedAt: new Date()
    });

    return {
      success: true,
      message: 'Priority updated successfully'
    };
  } catch (error) {
    console.error('Error updating priority:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Toggle emergency status
export const toggleEmergencyStatus = async (applicationId, emergency, updatedBy) => {
  try {
    const result = await getApplication(applicationId);
    if (!result.success) {
      return result;
    }

    const application = result.application;
    const applicationRef = doc(db, 'patientApplications', application.id);

    await updateDoc(applicationRef, {
      emergency: emergency,
      updatedAt: new Date(),
      emergencyUpdatedBy: updatedBy,
      emergencyUpdatedAt: new Date()
    });

    return {
      success: true,
      message: `Emergency status ${emergency ? 'enabled' : 'disabled'} successfully`
    };
  } catch (error) {
    console.error('Error updating emergency status:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get applications by status for admin review
export const getApplicationsByStatus = async (status, limit = 50) => {
  try {
    let q = query(
      collection(db, 'patientApplications'),
      where('status', '==', status),
      orderBy('submittedAt', 'desc')
    );

    if (limit) {
      q = query(q, limit(limit));
    }

    const querySnapshot = await getDocs(q);
    const applications = [];

    querySnapshot.forEach((doc) => {
      applications.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return {
      success: true,
      applications
    };
  } catch (error) {
    console.error('Error fetching applications by status:', error);
    return {
      success: false,
      error: error.message,
      applications: []
    };
  }
};

// Get applications requiring action (for workflow management)
export const getApplicationsRequiringAction = async (userRole, userId) => {
  try {
    let applications = [];

    switch (userRole) {
      case 'Doctor':
        // Get applications pending medical verification
        const medicalQuery = query(
          collection(db, 'patientApplications'),
          where('workflow.medicalVerification.status', '==', 'pending'),
          orderBy('submittedAt', 'desc')
        );
        const medicalSnapshot = await getDocs(medicalQuery);
        medicalSnapshot.forEach((doc) => {
          applications.push({
            id: doc.id,
            actionType: 'Medical Verification',
            ...doc.data()
          });
        });
        break;

      case 'PolicyWriter':
        // Get applications pending policy verification
        const policyQuery = query(
          collection(db, 'patientApplications'),
          where('workflow.policyVerification.status', '==', 'pending'),
          orderBy('submittedAt', 'desc')
        );
        const policySnapshot = await getDocs(policyQuery);
        policySnapshot.forEach((doc) => {
          applications.push({
            id: doc.id,
            actionType: 'Policy Verification',
            ...doc.data()
          });
        });
        break;

      case 'FinancialTeam':
        // Get approved applications for reimbursement processing
        const financeQuery = query(
          collection(db, 'patientApplications'),
          where('status', '==', 'approved'),
          orderBy('submittedAt', 'desc')
        );
        const financeSnapshot = await getDocs(financeQuery);
        financeSnapshot.forEach((doc) => {
          applications.push({
            id: doc.id,
            actionType: 'Reimbursement Processing',
            ...doc.data()
          });
        });
        break;

      case 'SuperAdmin':
        // Get all applications requiring any action
        const adminQuery = query(
          collection(db, 'patientApplications'),
          where('status', 'in', ['submitted', 'under_review', 'pending_documents']),
          orderBy('submittedAt', 'desc')
        );
        const adminSnapshot = await getDocs(adminQuery);
        adminSnapshot.forEach((doc) => {
          applications.push({
            id: doc.id,
            actionType: 'Admin Review',
            ...doc.data()
          });
        });
        break;

      default:
        break;
    }

    return {
      success: true,
      applications
    };
  } catch (error) {
    console.error('Error fetching applications requiring action:', error);
    return {
      success: false,
      error: error.message,
      applications: []
    };
  }
};

// Get application timeline/history
export const getApplicationTimeline = async (applicationId) => {
  try {
    const result = await getApplication(applicationId);
    if (!result.success) {
      return result;
    }

    const application = result.application;
    const timeline = [];

    // Add submission event
    timeline.push({
      event: 'Application Submitted',
      timestamp: application.submittedAt,
      description: `Application submitted by hospital`,
      type: 'submission',
      icon: 'FileText'
    });

    // Add workflow events
    if (application.workflow) {
      Object.entries(application.workflow).forEach(([workflowType, workflow]) => {
        if (workflow.verifiedAt) {
          timeline.push({
            event: `${workflowType.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}`,
            timestamp: workflow.verifiedAt,
            description: `${workflow.status === 'approved' ? 'Approved' : 'Rejected'} by ${workflow.verifiedBy}`,
            comments: workflow.comments,
            type: workflow.status,
            icon: workflow.status === 'approved' ? 'CheckCircle' : 'XCircle'
          });
        }
      });
    }

    // Add status change events
    if (application.statusHistory) {
      Object.entries(application.statusHistory).forEach(([status, statusData]) => {
        timeline.push({
          event: `Status Changed to ${status.replace('_', ' ').toUpperCase()}`,
          timestamp: statusData.updatedAt,
          description: `Updated by ${statusData.updatedBy}`,
          comments: statusData.comments,
          type: 'status_change',
          icon: 'Activity'
        });
      });
    }

    // Sort timeline by timestamp (newest first)
    timeline.sort((a, b) => {
      const aTime = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp);
      const bTime = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp);
      return bTime - aTime;
    });

    return {
      success: true,
      timeline
    };
  } catch (error) {
    console.error('Error fetching application timeline:', error);
    return {
      success: false,
      error: error.message,
      timeline: []
    };
  }
};

// Export application data for reporting
export const exportApplicationData = async (filters = {}) => {
  try {
    let q = collection(db, 'patientApplications');

    // Apply filters
    if (filters.hospitalId) {
      q = query(q, where('hospitalId', '==', filters.hospitalId));
    }
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }
    if (filters.emergency !== undefined) {
      q = query(q, where('emergency', '==', filters.emergency));
    }

    q = query(q, orderBy('submittedAt', 'desc'));

    const querySnapshot = await getDocs(q);
    const applications = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Flatten data for CSV export
      applications.push({
        applicationId: data.applicationId,
        patientName: data.patient?.name,
        patientAge: data.patient?.age,
        patientGender: data.patient?.gender,
        diagnosis: data.medical?.diagnosis,
        treatmentType: data.medical?.treatmentType,
        doctorName: data.medical?.doctor?.name,
        policyNumber: data.policy?.number,
        insuranceCompany: data.policy?.insuranceCompany,
        coverageAmount: data.policy?.coverageAmount,
        estimatedCost: data.medical?.estimatedCost,
        actualCost: data.medical?.actualCost,
        status: data.status,
        priority: data.priority,
        emergency: data.emergency,
        submittedAt: data.submittedAt?.toDate ? data.submittedAt.toDate().toISOString() : data.submittedAt,
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate().toISOString() : data.updatedAt
      });
    });

    return {
      success: true,
      applications,
      count: applications.length
    };
  } catch (error) {
    console.error('Error exporting application data:', error);
    return {
      success: false,
      error: error.message,
      applications: []
    };
  }
};

// Bulk update applications (for admin operations)
export const bulkUpdateApplications = async (applicationIds, updateData, updatedBy) => {
  try {
    const updatePromises = applicationIds.map(async (applicationId) => {
      const result = await getApplication(applicationId);
      if (result.success) {
        const applicationRef = doc(db, 'patientApplications', result.application.id);
        return updateDoc(applicationRef, {
          ...updateData,
          updatedAt: new Date(),
          bulkUpdatedBy: updatedBy,
          bulkUpdatedAt: new Date()
        });
      }
    });

    await Promise.all(updatePromises);

    return {
      success: true,
      message: `Successfully updated ${applicationIds.length} applications`
    };
  } catch (error) {
    console.error('Error bulk updating applications:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Upload a discharge document and attach to the application
export const uploadDischargeDocument = async (
  applicationId,
  file,
  title,
  metadata = {}
) => {
  try {
    // Handle linked documents (no file upload needed)
    if (metadata.isLinkedDocument && metadata.scrutinyDocument) {
      const appResult = await getApplication(applicationId);
      if (!appResult.success) {
        throw new Error('Application not found');
      }
      const appDocId = appResult.application.id;

      // Use the provided scrutiny document data
      const documentData = {
        ...metadata.scrutinyDocument,
        id: `dis_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        uploadedAt: new Date(),
        uploadedBy: 'hospital',
        uploaderName: 'Hospital Staff'
      };

      // Append to application using arrayUnion into top-level dischargeDocuments
      const applicationRef = doc(db, 'patientApplications', appDocId);
      await updateDoc(applicationRef, {
        dischargeDocuments: arrayUnion(documentData),
        updatedAt: new Date(),
      });

      return { success: true, document: documentData };
    }

    // Regular file upload
    if (!file) throw new Error('No file provided');
    if (!title || !title.trim()) throw new Error('Document title is required');

    // Resolve actual Firestore doc id
    const appResult = await getApplication(applicationId);
    if (!appResult.success) {
      throw new Error('Application not found');
    }
    const appDocId = appResult.application.id;

    // Build storage path
    const safePatient = (appResult.application.patient?.name || 'patient').replace(/\s+/g, '_');
    const safeTitle = title.replace(/[^a-zA-Z0-9_-]+/g, '_');
    const ts = Date.now();
    const uniqueToken = Math.random().toString(36).slice(2, 8);
    const fileName = `${safeTitle}_${ts}_${uniqueToken}_${file.name}`;
    const storagePath = `applications/${appResult.application.hospitalId}/${safePatient}/discharge/${fileName}`;

    // Upload to Firebase Storage
    const storageRef = ref(storage, storagePath);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);

    // Prepare document metadata
    const documentData = {
      id: `dis_${ts}_${Math.random().toString(36).slice(2, 6)}`,
      title: title.trim(),
      type: title.trim(),
      description: (metadata.description || '').trim(),
      url: downloadURL,
      fileName: file.name,
      size: file.size,
      uploadedAt: new Date(),
      uploadedBy: metadata.uploadedBy || 'hospital',
      uploaderName: metadata.uploaderName || '',
      contentType: file.type || 'application/octet-stream'
    };

    // Initialize dispatch scrutiny fields
    documentData.dispatchStatus = 'pending'; // 'pending' | 'approved' | 'rejected'
    documentData.dispatchReviewedBy = '';
    documentData.dispatchReviewerName = '';
    documentData.dispatchReviewedAt = null;
    documentData.dispatchRemarks = '';

    // Append to application using arrayUnion into top-level dischargeDocuments
    const applicationRef = doc(db, 'patientApplications', appDocId);
    await updateDoc(applicationRef, {
      dischargeDocuments: arrayUnion(documentData),
      updatedAt: new Date(),
    });

    // Email suppressed on upload to avoid spam. Notification will be sent on "Send for Doctor Review".

    return { success: true, document: documentData };
  } catch (error) {
    console.error('Error uploading discharge document:', error);
    return { success: false, error: error.message };
  }
};

// Attach an existing discharge/dispatch document to the main documents map so it appears in Documents tab
export const attachDischargeDocToDocuments = async (applicationId, documentId, keyOrTitle = '') => {
  try {
    const res = await getApplication(applicationId);
    if (!res.success) return res;
    const app = res.application;
    const appRef = doc(db, 'patientApplications', app.id);

    // Find the document in dischargeDocuments or dispatchDocuments by id
    const listA = Array.isArray(app.dischargeDocuments) ? app.dischargeDocuments : [];
    const listB = Array.isArray(app.dispatchDocuments) ? app.dispatchDocuments : [];
    const all = [...listA, ...listB];
    const docItem = all.find(d => d.id === documentId) || null;
    if (!docItem) {
      return { success: false, error: 'Document not found' };
    }

    // Build a safe key for documents map
    const rawKey = (keyOrTitle || docItem.type || docItem.title || docItem.fileName || 'Document').toString();
    const safeKey = rawKey
      .trim()
      .replace(/\s+/g, ' ')
      .replace(/[^a-zA-Z0-9 _-]/g, '')
      .replace(/\s/g, '_');

    // Minimal document payload for Documents tab
    const docPayload = {
      url: docItem.url,
      fileName: docItem.fileName || docItem.title || `${safeKey}.pdf`,
      size: docItem.size || 0,
      uploadedAt: docItem.uploadedAt || new Date(),
      uploadedBy: docItem.uploadedBy || 'hospital',
      uploaderName: docItem.uploaderName || 'Hospital',
      type: docItem.type || 'document'
    };

    // Update nested field documents.safeKey
    await updateDoc(appRef, {
      [`documents.${safeKey}`]: docPayload,
      updatedAt: new Date()
    });

    return { success: true, key: safeKey, document: docPayload };
  } catch (error) {
    console.error('Error attaching document to documents map:', error);
    return { success: false, error: error.message };
  }
};

// Get application metrics for analytics
export const getApplicationMetrics = async (hospitalId = null, dateRange = null) => {
  try {
    let q = collection(db, 'patientApplications');

    if (hospitalId) {
      q = query(q, where('hospitalId', '==', hospitalId));
    }

    if (dateRange && dateRange.start && dateRange.end) {
      q = query(
        q,
        where('submittedAt', '>=', Timestamp.fromDate(new Date(dateRange.start))),
        where('submittedAt', '<=', Timestamp.fromDate(new Date(dateRange.end)))
      );
    }

    const querySnapshot = await getDocs(q);

    const metrics = {
      totalApplications: 0,
      statusDistribution: {
        submitted: 0,
        under_review: 0,
        approved: 0,
        rejected: 0,
        pending_documents: 0
      },
      priorityDistribution: {
        normal: 0,
        high: 0,
        urgent: 0
      },
      treatmentTypeDistribution: {
        medical: 0,
        surgical: 0
      },
      emergencyApplications: 0,
      totalClaimAmount: 0,
        // Note: Email notifications are intentionally NOT sent on each upload to avoid spam.
        // An email will be sent when the hospital clicks "Send for Doctor Review".
      averageProcessingTime: 0,
      topInsuranceCompanies: {},
      topDiagnoses: {}
    };

    let totalProcessingTime = 0;
    let processedApplications = 0;

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      metrics.totalApplications++;

      // Status distribution
      if (metrics.statusDistribution.hasOwnProperty(data.status)) {
        metrics.statusDistribution[data.status]++;
      }

      // Priority distribution
      if (metrics.priorityDistribution.hasOwnProperty(data.priority)) {
        metrics.priorityDistribution[data.priority]++;
      }

      // Treatment type distribution
      if (data.medical?.treatmentType && metrics.treatmentTypeDistribution.hasOwnProperty(data.medical.treatmentType)) {
        metrics.treatmentTypeDistribution[data.medical.treatmentType]++;
      }

      // Emergency applications
      if (data.emergency) {
        metrics.emergencyApplications++;
      }

      // Claim amounts
      const claimAmount = data.medical?.estimatedCost || 0;
      metrics.totalClaimAmount += claimAmount;

      // Processing time (for approved/rejected applications)
      if ((data.status === 'approved' || data.status === 'rejected') && data.submittedAt && data.updatedAt) {
        const submittedTime = data.submittedAt.toDate ? data.submittedAt.toDate() : new Date(data.submittedAt);
        const updatedTime = data.updatedAt.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt);
        const processingTime = (updatedTime - submittedTime) / (1000 * 60 * 60 * 24); // in days
        totalProcessingTime += processingTime;
        processedApplications++;
      }

      // Top insurance companies
      const insuranceCompany = data.policy?.insuranceCompany;
      if (insuranceCompany) {
        metrics.topInsuranceCompanies[insuranceCompany] = (metrics.topInsuranceCompanies[insuranceCompany] || 0) + 1;
      }

      // Top diagnoses
      const diagnosis = data.medical?.diagnosis;
      if (diagnosis) {
        metrics.topDiagnoses[diagnosis] = (metrics.topDiagnoses[diagnosis] || 0) + 1;
      }
    });

    // Calculate averages
    metrics.averageClaimAmount = metrics.totalApplications > 0 ?
      Math.round(metrics.totalClaimAmount / metrics.totalApplications) : 0;

    metrics.averageProcessingTime = processedApplications > 0 ?
      Math.round(totalProcessingTime / processedApplications * 10) / 10 : 0;

    // Sort and limit top lists
    metrics.topInsuranceCompanies = Object.entries(metrics.topInsuranceCompanies)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {});

    metrics.topDiagnoses = Object.entries(metrics.topDiagnoses)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {});

    return {
      success: true,
      metrics
    };
  } catch (error) {
    console.error('Error fetching application metrics:', error);
    return {
      success: false,
      error: error.message,
      metrics: null
    };
  }
};

// ===== DISPATCH DOCUMENTS (Discharge) =====
// Create a dispatch record for discharge documents
export const createDocumentDispatch = async (applicationId, dispatchPayload) => {
  try {
    const result = await getApplication(applicationId);
    if (!result.success) return result;

    const app = result.application;
    const appRef = doc(db, 'patientApplications', app.id);
    const dispatchRef = await addDoc(collection(appRef, 'dispatches'), {
      applicationId,
      hospitalId: app.hospitalId,
      documents: dispatchPayload.documents || [], // [{id,title,url,type}]
      mode: dispatchPayload.mode, // Courier, Speed Post, Hand Delivery, Email
      dispatchedAt: dispatchPayload.dispatchedAt
        ? (dispatchPayload.dispatchedAt?.toDate ? dispatchPayload.dispatchedAt : Timestamp.fromDate(new Date(dispatchPayload.dispatchedAt)))
        : Timestamp.now(),
      trackingId: dispatchPayload.trackingId || '',
      notes: dispatchPayload.notes || '',
      // Proof of Delivery (POD) metadata if provided by hospital
      pod: dispatchPayload.pod || null, // { url, name, size, type, uploadedAt, uploadedBy, uploaderName, storagePath }
      status: 'dispatched',
      createdAt: Timestamp.now(),
      createdBy: dispatchPayload.createdBy || 'hospital',
    });

    // Send a single email to Operations team upon dispatch submission
    try {
      const patientName = app?.patient?.name || 'Patient';
      const appIdStr = app?.applicationId || applicationId;
      const docs = Array.isArray(dispatchPayload.documents) ? dispatchPayload.documents : [];
  const subject = `Dispatch Packet Submitted – ${patientName} (ID: ${appIdStr})`;
      const patientLink = getPatientApplicationLinkHtml(
        appIdStr,
        patientName,
        'cssg@samhealth.co.in',
        'finance',
        `View Patient Application for ${patientName} (${appIdStr})`
      );
      const html = `<!DOCTYPE html><html><body style="font-family:Arial,Helvetica,sans-serif;line-height:1.6;color:#0f172a;">
  <h2 style=\"margin:0 0 12px;color:#065f46;\">Dispatch Packet Submitted</h2>
  <p style=\"margin:0 0 8px;\">Hospital has submitted the <strong>dispatch packet</strong> for <strong>${patientName}</strong> (ID: <strong>${appIdStr}</strong>).</p>
        <ul style=\"margin:8px 0 8px 18px;\">
          <li>Mode: <strong>${dispatchPayload.mode || '-'}</strong></li>
          <li>Tracking ID: <strong>${dispatchPayload.trackingId || '-'}</strong></li>
          <li>Dispatch documents: <strong>${docs.length}</strong></li>
        </ul>
        ${dispatchPayload.notes ? `<p style=\"margin:0 0 8px;\">Notes: ${dispatchPayload.notes.replace(/</g,'&lt;')}</p>` : ''}
        ${patientLink}
        <p style=\"margin:12px 0 0;font-size:12px;color:#64748b;\">This is an automated notification.</p>
      </body></html>`;
      await sendEmail({ to: 'cssg@samhealth.co.in', subject, html });
    } catch (e) {
      console.warn('Non-blocking email failed (createDocumentDispatch):', e?.message || e);
    }

    // Update bucket filter - dispatch initiated
    await updateRoleBucket(app.id, 'finance', 'dispatchInitiated');

    return { success: true, id: dispatchRef.id };
  } catch (error) {
    console.error('Error creating document dispatch:', error);
    return { success: false, error: error.message };
  }
};

// Get dispatch records for an application
export const getDocumentDispatches = async (applicationId) => {
  try {
    const result = await getApplication(applicationId);
    if (!result.success) return result;
    const app = result.application;
    const appRef = doc(db, 'patientApplications', app.id);
    const q = query(collection(appRef, 'dispatches'), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    const dispatches = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    return { success: true, dispatches };
  } catch (error) {
    console.error('Error getting document dispatches:', error);
    return { success: false, error: error.message, dispatches: [] };
  }
};

// Mark a dispatch as received (to be used by backoffice/finance UI)
export const markDispatchReceived = async (applicationId, dispatchId, payload = 'system') => {
  try {
    const result = await getApplication(applicationId);
    if (!result.success) return result;
    const app = result.application;
    const appRef = doc(db, 'patientApplications', app.id);
    const dRef = doc(collection(appRef, 'dispatches'), dispatchId);

    // Backward compatibility: if payload is a string, treat it as receivedBy
    const receivedBy = typeof payload === 'string' ? payload : (payload.receivedBy || 'system');
    const receivedAt = (typeof payload === 'object' && payload.receivedAt)
      ? (payload.receivedAt?.toDate ? payload.receivedAt : Timestamp.fromDate(new Date(payload.receivedAt)))
      : Timestamp.now();
    const receiverNotes = typeof payload === 'object' ? (payload.receiverNotes || '') : '';

    await updateDoc(dRef, {
      status: 'received',
      receivedAt,
      receivedBy,
      receiverNotes,
      updatedAt: Timestamp.now()
    });
    return { success: true };
  } catch (error) {
    console.error('Error marking dispatch received:', error);
    return { success: false, error: error.message };
  }
};

// Get all dispatch records across applications (for finance/backoffice)
export const getAllDispatches = async (filters = {}) => {
  try {
    let q = collectionGroup(db, 'dispatches');
    // Optional status filter
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }
    // Optional hospital filter
    if (filters.hospitalId) {
      q = query(q, where('hospitalId', '==', filters.hospitalId));
    }
    // Order newest first
    q = query(q, orderBy('createdAt', 'desc'));

    const snapshot = await getDocs(q);
    const dispatches = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    return { success: true, dispatches };
  } catch (error) {
    console.error('Error fetching all dispatches:', error);
    return { success: false, error: error.message, dispatches: [] };
  }
};

// Get discharge cases for a hospital for a specific date (string 'YYYY-MM-DD' or Date)
export const getHospitalDischargeCases = async (hospitalId, options = {}) => {
  try {
    if (!hospitalId) {
      return { success: false, error: 'Missing hospitalId', applications: [] };
    }

    const { targetDate, pageSize } = options;
    const lim = Math.max(1, Math.min(parseInt(pageSize || 100, 10), 500));

    const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
    const toYMD = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    let targetDateObj;
    let targetDateStr;
    if (!targetDate) {
      // default to tomorrow (local time)
      const now = new Date();
      targetDateObj = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      targetDateStr = toYMD(targetDateObj);
    } else if (typeof targetDate === 'string') {
      targetDateStr = targetDate;
      const [y, m, d] = targetDate.split('-').map((v) => parseInt(v, 10));
      targetDateObj = new Date(y, (m || 1) - 1, d || 1);
    } else if (targetDate instanceof Date) {
      targetDateObj = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
      targetDateStr = toYMD(targetDateObj);
    } else {
      return { success: false, error: 'Invalid targetDate', applications: [] };
    }

    // Build a start/end range for Timestamp-based storage
    const startOfDay = new Date(targetDateObj.getFullYear(), targetDateObj.getMonth(), targetDateObj.getDate(), 0, 0, 0, 0);
    const endOfDay = new Date(targetDateObj.getFullYear(), targetDateObj.getMonth(), targetDateObj.getDate(), 23, 59, 59, 999);
    const startTs = Timestamp.fromDate(startOfDay);
    const endTs = Timestamp.fromDate(endOfDay);

    // Query 1: string-based dischargeDate equality (common in current schema)
    const qString = query(
      collection(db, 'patientApplications'),
      where('hospitalId', '==', hospitalId),
      where('medical.dischargeDate', '==', targetDateStr),
      limit(lim)
    );

    // Query 2: timestamp-based dischargeDate within the target day
    const qTimestamp = query(
      collection(db, 'patientApplications'),
      where('hospitalId', '==', hospitalId),
      where('medical.dischargeDate', '>=', startTs),
      where('medical.dischargeDate', '<=', endTs),
      limit(lim)
    );

    const [qs1, qs2] = await Promise.all([getDocs(qString), getDocs(qTimestamp)]);

    const map = new Map();
    qs1.forEach((docSnap) => map.set(docSnap.id, { id: docSnap.id, ...docSnap.data() }));
    qs2.forEach((docSnap) => map.set(docSnap.id, { id: docSnap.id, ...docSnap.data() }));

    const applications = Array.from(map.values());

    // Optional: sort by priority (urgent > high > normal) then by submittedAt desc
    const prioRank = { urgent: 3, high: 2, normal: 1 };
    applications.sort((a, b) => {
      const pa = prioRank[a.priority] || 0;
      const pb = prioRank[b.priority] || 0;
      if (pb !== pa) return pb - pa;
      const at = a.submittedAt?.toDate ? a.submittedAt.toDate() : new Date(a.submittedAt || 0);
      const bt = b.submittedAt?.toDate ? b.submittedAt.toDate() : new Date(b.submittedAt || 0);
      return bt - at;
    });

    return { success: true, applications, count: applications.length, targetDate: targetDateStr };
  } catch (error) {
    console.error('Error fetching hospital discharge cases:', error);
    return { success: false, error: error.message, applications: [] };
  }
};

// Validate application data before submission
export const validateApplicationData = (applicationData) => {
  const errors = [];

  // Validate patient information
  if (!applicationData.patientName?.trim()) {
    errors.push('Patient name is required');
  }
  if (!applicationData.patientAge || applicationData.patientAge < 0 || applicationData.patientAge > 120) {
    errors.push('Valid patient age is required');
  }
  if (!applicationData.patientGender) {
    errors.push('Patient gender is required');
  }
  if (!applicationData.patientPhone?.match(/^\d{10}$/)) {
    errors.push('Valid 10-digit phone number is required');
  }
  if (!applicationData.patientAadhar?.match(/^\d{12}$/)) {
    errors.push('Valid 12-digit Aadhar number is required');
  }

  // Validate medical information
  if (!applicationData.diagnosis?.trim()) {
    errors.push('Diagnosis is required');
  }
  if (!applicationData.treatmentType) {
    errors.push('Treatment type is required');
  }
  if (!applicationData.doctorName?.trim()) {
    errors.push('Doctor name is required');
  }
  if (!applicationData.admissionDate) {
    errors.push('Admission date is required');
  }
  if (!applicationData.estimatedCost || applicationData.estimatedCost <= 0) {
    errors.push('Valid estimated cost is required');
  }

  // Validate policy information
  if (!applicationData.policyNumber?.trim()) {
    errors.push('Policy number is required');
  }
  if (!applicationData.insuranceCompany?.trim()) {
    errors.push('Insurance company is required');
  }
  if (!applicationData.coverageAmount || applicationData.coverageAmount <= 0) {
    errors.push('Valid coverage amount is required');
  }
  if (!applicationData.policyStartDate || !applicationData.policyEndDate) {
    errors.push('Policy start and end dates are required');
  }

  // Validate policy holder information (if not self)
  if (!applicationData.isSelf) {
    if (!applicationData.policyHolderName?.trim()) {
      errors.push('Policy holder name is required');
    }
    if (!applicationData.relationshipWithPatient?.trim()) {
      errors.push('Relationship with patient is required');
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Record hospital acknowledgement (accept/challenge) for a specific sanction letter
export const acknowledgeSanctionLetter = async (applicationId, letterId, { status, comment = '', hospitalUserId, hospitalUserName = '' }) => {
  try {
    if (!['accepted', 'challenged'].includes(status)) {
      return { success: false, error: 'Invalid acknowledgement status' };
    }

    const sanctionRef = doc(db, 'sanctions', letterId);
    // Update sanction document
    await updateDoc(sanctionRef, {
      hospitalAcknowledgement: {
        status,
        comment: comment || '',
        by: hospitalUserId || null,
        byName: hospitalUserName || null,
        at: new Date()
      }
    });

    // Also mirror the latest acknowledgement on application workflow for quick checks
    const appRes = await getApplication(applicationId);
    if (appRes.success) {
      const appRef = doc(db, 'patientApplications', appRes.application.id);
      await updateDoc(appRef, {
        [`workflow.acknowledgements.${letterId}`]: {
          type: 'sanction',
          status,
          comment: comment || '',
          at: new Date()
        },
        updatedAt: new Date()
      });

      // Update bucket filter when FAL is acknowledged
      // Need to check if this is an approval letter (FAL)
      const sanctionDoc = await getDoc(sanctionRef);
      if (sanctionDoc.exists() && sanctionDoc.data().letterType === 'approval') {
        if (status === 'accepted') {
          // FAL accepted -> move to FAL approved
          await updateRoleBucket(appRes.application.id, 'doctor', 'fal_approved');
          await updateRoleBucket(appRes.application.id, 'finance', 'falAccepted');
          await updateRoleBucket(appRes.application.id, 'hospital', 'approved');
        } else if (status === 'challenged') {
          // FAL challenged -> discharge query raised
          await updateRoleBucket(appRes.application.id, 'doctor', 'discharge_query_reply_received');
          await updateRoleBucket(appRes.application.id, 'hospital', 'fal_challenged');
        }
      }
    }

    return { success: true };
  } catch (error) {
    console.error('Error acknowledging letter:', error);
    return { success: false, error: error.message };
  }
};

// Record hospital acknowledgement for KYP (since KYP is stored in workflow)
export const acknowledgeKYP = async (applicationId, { status, comment = '', hospitalUserId, hospitalUserName = '' }) => {
  try {
    if (!['accepted', 'challenged'].includes(status)) {
      return { success: false, error: 'Invalid acknowledgement status' };
    }

    const res = await getApplication(applicationId);
    if (!res.success) return res;
    const appRef = doc(db, 'patientApplications', res.application.id);
    await updateDoc(appRef, {
      'workflow.medicalVerification.kypHospitalAcknowledgement': {
        status,
        comment: comment || '',
        by: hospitalUserId || null,
        byName: hospitalUserName || null,
        at: new Date()
      },
      updatedAt: new Date()
    });

    // Update bucket filter based on hospital acknowledgement
    if (status === 'accepted') {
      // Hospital accepted KYP -> discharge pending
      await updateRoleBucket(res.application.id, 'doctor', 'discharge_pending');
      await updateRoleBucket(res.application.id, 'hospital', 'kyp_accepted');
    } else if (status === 'challenged') {
      // Hospital challenged KYP -> kyp_challenged
      await updateRoleBucket(res.application.id, 'doctor', 'kyp_challenged');
      await updateRoleBucket(res.application.id, 'hospital', 'kyp_challenged');
    }

    return { success: true };
  } catch (error) {
    console.error('Error acknowledging KYP:', error);
    return { success: false, error: error.message };
  }
};

// ===== RISK FLAGGING (Doctor) =====
// Set or clear application risk flag with an accompanying note
export const setApplicationRisk = async (
  applicationId,
  { flagged, comment = '', by, byName = '' }
) => {
  try {
    const appRes = await getApplication(applicationId);
    if (!appRes.success) return appRes;
    const appRef = doc(db, 'patientApplications', appRes.application.id);

    const note = {
      id: `risk_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      action: flagged ? 'flag' : 'unflag',
      comment: (comment || '').trim(),
      by: by || null,
      byName: byName || '',
      at: new Date()
    };

    // Prepare risk payload
    const riskUpdate = {
      'risk.flagged': !!flagged,
      'risk.flaggedBy': flagged ? (by || null) : null,
      'risk.flaggedByName': flagged ? (byName || '') : '',
      'risk.flaggedAt': new Date(),
      'risk.comment': (comment || '').trim(),
      updatedAt: new Date()
    };

    // Append to notes history
    await updateDoc(appRef, {
      ...riskUpdate,
      'risk.notes': arrayUnion(note)
    });

    return { success: true, risk: { ...appRes.application.risk, ...{ flagged, flaggedBy: by, flaggedByName: byName, flaggedAt: new Date(), comment }, notes: [...(appRes.application.risk?.notes || []), note] } };
  } catch (error) {
    console.error('Error setting application risk:', error);
    return { success: false, error: error.message };
  }
};

// Get risk info for an application (status + notes)
export const getApplicationRisk = async (applicationId) => {
  try {
    const appRes = await getApplication(applicationId);
    if (!appRes.success) return appRes;
    return { success: true, risk: appRes.application.risk || null };
  } catch (error) {
    console.error('Error getting application risk:', error);
    return { success: false, error: error.message, risk: null };
  }
};

// Request doctor to review uploaded discharge/dispatch documents
export const requestDispatchReview = async (applicationId, { note = '', requestedBy = 'hospital', requestedByName = '' } = {}) => {
  try {
    const result = await getApplication(applicationId);
    if (!result.success) return result;
    const appRef = doc(db, 'patientApplications', result.application.id);
    await updateDoc(appRef, {
      'workflow.dischargeSettlement.dispatchReview': {
        status: 'requested',
        note: (note || '').trim(),
        by: requestedBy,
        byName: requestedByName,
        at: Timestamp.now()
      },
      updatedAt: Timestamp.now()
    });

    // Optional: could trigger a notification via notificationService here
    return { success: true };
  } catch (error) {
    console.error('Error requesting dispatch review:', error);
    return { success: false, error: error.message };
  }
};

// Upload a dispatch document (separate from discharge) and attach to the application
// NOTE: These are not reviewed by doctors; used for final dispatch packet (e.g., claim form, KYC, ID proofs)
export const uploadDispatchDocument = async (
  applicationId,
  file,
  title,
  metadata = {}
) => {
  try {
    if (!file) throw new Error('No file provided');
    const cleanTitle = (title || file.name || 'Dispatch Document').toString().trim();
    if (!cleanTitle) throw new Error('Document title is required');

    // Resolve actual Firestore doc id
    const appResult = await getApplication(applicationId);
    if (!appResult.success) {
      throw new Error('Application not found');
    }
    const appDocId = appResult.application.id;

    // Build storage path (under dispatch/)
    const safePatient = (appResult.application.patient?.name || 'patient').replace(/\s+/g, '_');
    const safeTitle = cleanTitle.replace(/[^a-zA-Z0-9_-]+/g, '_');
    const ts = Date.now();
    const uniqueToken = Math.random().toString(36).slice(2, 8);
    const fileName = `${safeTitle}_${ts}_${uniqueToken}_${file.name}`;
    const storagePath = `applications/${appResult.application.hospitalId}/${safePatient}/dispatch/${fileName}`;

    const storageRef = ref(storage, storagePath);
    // Use resumable upload for large files (supports 100MB+ reliably)
    const task = uploadBytesResumable(storageRef, file);
    await new Promise((resolve, reject) => {
      task.on('state_changed', undefined, reject, () => resolve());
    });
    const downloadURL = await getDownloadURL(task.snapshot.ref);

    const documentData = {
      id: `dd_${ts}_${Math.random().toString(36).slice(2, 6)}`,
      title: cleanTitle,
      type: (metadata.type || cleanTitle),
      description: (metadata.description || '').trim(),
      url: downloadURL,
      fileName: file.name,
      size: file.size,
      uploadedAt: new Date(),
      uploadedBy: metadata.uploadedBy || 'hospital',
      uploaderName: metadata.uploaderName || '',
      contentType: file.type || 'application/octet-stream'
    };

    const applicationRef = doc(db, 'patientApplications', appDocId);
    await updateDoc(applicationRef, {
      dispatchDocuments: arrayUnion(documentData),
      updatedAt: new Date(),
    });

    return { success: true, document: documentData };
  } catch (error) {
    console.error('Error uploading dispatch document:', error);
    return { success: false, error: error.message };
  }
};

// ===== SCRUTINY CHECKLIST (Hospital) =====
// Default 17-item scrutiny list (can be customized later)
const DEFAULT_SCRUTINY_ITEMS = [
  'Discharge Summary',
  'Final Consolidated Bill',
  'Detailed Itemized Bills',
  'Procedure/OT Notes',
  'Doctor\'s Prescriptions',
  'Investigation/Diagnostics Reports',
  'Implant Invoice (if applicable)',
  'Pharmacy Bills',
  'Nursing/Room/Bed Charges',
  'Authorization/Pre-auth Letter',
  'Insurance Claim Form',
  'Policy Copy',
  'KYC Set (Patient/Policy Holder)',
  'ID Proof Copy',
  'Cancelled Cheque/Bank Proof',
  'Discharge Clearance/Consent',
  'Any Other Supporting'
].map((title, idx) => ({
  id: `scr_${idx + 1}`,
  title,
  // Do not force mandatory by default; let maker-checker confirm completeness
  mandatory: false,
  status: 'pending', // pending | attached | validated
  attachments: [],   // [{ source: 'dischargeDocuments'|'dispatchDocuments', id, title }]
  note: ''
}));

// Initialize or fetch scrutiny checklist on application.workflow.dischargeSettlement.scrutinyChecklist
export const getOrInitScrutinyChecklist = async (applicationId) => {
  try {
    const res = await getApplication(applicationId);
    if (!res.success) return res;
    const app = res.application;
    const existing = app?.workflow?.dischargeSettlement?.scrutinyChecklist;
    if (Array.isArray(existing) && existing.length > 0) {
      return { success: true, items: existing };
    }
    // Seed default
    const appRef = doc(db, 'patientApplications', app.id);
    const items = DEFAULT_SCRUTINY_ITEMS;
    await updateDoc(appRef, {
      'workflow.dischargeSettlement.scrutinyChecklist': items,
      updatedAt: new Date()
    });
    return { success: true, items };
  } catch (e) {
    console.error('Error init scrutiny checklist:', e);
    return { success: false, error: e.message, items: [] };
  }
};

// Attach an existing uploaded document (from discharge/dispatch) to a scrutiny item
export const attachExistingDocToScrutiny = async (applicationId, itemId, { source, docId, title = '' }) => {
  try {
    if (!['dischargeDocuments', 'dispatchDocuments'].includes(source)) {
      return { success: false, error: 'Invalid source' };
    }
    const res = await getApplication(applicationId);
    if (!res.success) return res;
    const app = res.application;
    const items = (app?.workflow?.dischargeSettlement?.scrutinyChecklist || []).map((it) => ({ ...it }));
    const idx = items.findIndex((it) => it.id === itemId);
    if (idx === -1) return { success: false, error: 'Item not found' };

    const att = items[idx].attachments || [];
    const already = att.some((a) => a.source === source && a.id === docId);
    if (!already) {
      att.push({ source, id: docId, title });
    }
    items[idx].attachments = att;
    items[idx].status = 'attached';

    const appRef = doc(db, 'patientApplications', app.id);
    await updateDoc(appRef, {
      'workflow.dischargeSettlement.scrutinyChecklist': items,
      updatedAt: new Date()
    });
    return { success: true, item: items[idx] };
  } catch (e) {
    console.error('Error attaching doc to scrutiny:', e);
    return { success: false, error: e.message };
  }
};

// Update a scrutiny item (e.g., mark validated or add note)
export const updateScrutinyItem = async (applicationId, itemId, { status, note }) => {
  try {
    const res = await getApplication(applicationId);
    if (!res.success) return res;
    const app = res.application;
    const items = (app?.workflow?.dischargeSettlement?.scrutinyChecklist || []).map((it) => ({ ...it }));
    const idx = items.findIndex((it) => it.id === itemId);
    if (idx === -1) return { success: false, error: 'Item not found' };
    if (status) items[idx].status = status; // expected: 'validated' | 'pending' | 'attached'
    if (note !== undefined) items[idx].note = (note || '').trim();
    const appRef = doc(db, 'patientApplications', app.id);
    await updateDoc(appRef, {
      'workflow.dischargeSettlement.scrutinyChecklist': items,
      updatedAt: new Date()
    });
    return { success: true, item: items[idx] };
  } catch (e) {
    console.error('Error updating scrutiny item:', e);
    return { success: false, error: e.message };
  }
};

// Submit scrutiny checklist for review/confirmation
export const submitScrutinyChecklist = async (applicationId, { by = 'hospital', byName = '' } = {}) => {
  try {
    const res = await getApplication(applicationId);
    if (!res.success) return res;
    const appRef = doc(db, 'patientApplications', res.application.id);
    await updateDoc(appRef, {
      'workflow.dischargeSettlement.scrutinySubmission': {
        status: 'submitted',
        by,
        byName,
        at: new Date()
      },
      'workflow.dischargeSettlement.scrutinyChecklistLocked': true,
      updatedAt: new Date()
    });

    // Update bucket filter - scrutiny submitted, pending review
    await updateRoleBucket(res.application.id, 'doctor', 'scrutiny_pending');

    // Send an email to Doctors team when scrutiny is submitted for review
    try {
      const app = res.application || {};
      const patientName = app?.patient?.name || 'Patient';
      const appIdStr = app?.applicationId || applicationId;
      const docs = Array.isArray(app?.dischargeDocuments) ? app.dischargeDocuments : [];
      const dispatchDocs = Array.isArray(app?.dispatchDocuments) ? app.dispatchDocuments : [];
  const uploadedCount = docs.length + dispatchDocs.length;
  const subject = `Scrutiny Submitted for Doctor Review – ${patientName} (ID: ${appIdStr})`;
      const patientLink = getPatientApplicationLinkHtml(
        appIdStr,
        patientName,
        'doctors@samhealth.co.in',
        'doctor',
        `View Patient Application for ${patientName} (${appIdStr})`
      );
  const html = `<!DOCTYPE html><html><body style=\"font-family:Arial,Helvetica,sans-serif;line-height:1.6;color:#0f172a;\">\n        <h2 style=\"margin:0 0 12px;color:#065f46;\">Scrutiny Submitted for Doctor Review</h2>\n        <p style=\"margin:0 8px 8px 0;\">Hospital <strong>${byName || 'Hospital'}</strong> has submitted the <strong>scrutiny packet</strong> for <strong>${patientName}</strong> (ID: <strong>${appIdStr}</strong>).</p>\n        <ul style=\"margin:8px 0 8px 18px;\">\n          <li>Total scrutiny-related uploads: <strong>${uploadedCount}</strong></li>\n          <li>Breakdown – Discharge docs: <strong>${docs.length}</strong>; Dispatch docs: <strong>${dispatchDocs.length}</strong></li>\n        </ul>\n        ${patientLink}\n        <p style=\"margin:12px 0 0;font-size:12px;color:#64748b;\">This is an automated notification.</p>\n      </body></html>`;
      await sendEmail({ to: 'doctors@samhealth.co.in', subject, html });
    } catch (e) {
      console.warn('Non-blocking email failed (submitScrutinyChecklist):', e?.message || e);
    }
    return { success: true };
  } catch (e) {
    console.error('Error submitting scrutiny checklist:', e);
    return { success: false, error: e.message };
  }
};

// Initiate discharge review - notify doctors when discharge documents are uploaded
export const initiateDischargeReview = async (applicationId, { by = 'hospital', byName = '' } = {}) => {
  try {
    const res = await getApplication(applicationId);
    if (!res.success) return res;
    
    const app = res.application || {};
    const docs = Array.isArray(app?.dischargeDocuments) ? app.dischargeDocuments : [];
    
    if (docs.length === 0) {
      return { success: false, error: 'No discharge documents uploaded yet' };
    }
    
    const appRef = doc(db, 'patientApplications', res.application.id);
    await updateDoc(appRef, {
      'workflow.dischargeSettlement.dischargeInitiated': {
        status: 'initiated',
        by,
        byName,
        at: new Date(),
        documentCount: docs.length
      },
      updatedAt: new Date()
    });

    // Update bucket filter - discharge initiated
    await updateRoleBucket(res.application.id, 'doctor', 'discharge_pending');
    
    // Send email notification to doctor team
    try {
      const patientName = app?.patient?.name || 'Patient';
      const appIdStr = app?.applicationId || applicationId;
      const hospitalName = app?.hospitalName || byName || 'Hospital';
      
      const subject = `Discharge Initiated – ${patientName} (ID: ${appIdStr})`;
      const patientLink = getPatientApplicationLinkHtml(
        appIdStr,
        patientName,
        'doctors@samhealth.co.in',
        'doctor',
        `View Patient Application for ${patientName} (${appIdStr})`
      );
      const html = `<!DOCTYPE html><html><body style="font-family:Arial,Helvetica,sans-serif;line-height:1.6;color:#0f172a;">
        <h2 style="margin:0 0 12px;color:#065f46;">Discharge Process Initiated</h2>
        <p style="margin:0 8px 8px 0;"><strong>${hospitalName}</strong> has initiated the discharge process for patient <strong>${patientName}</strong> (Application ID: <strong>${appIdStr}</strong>).</p>
        <div style="background:#f1f5f9;padding:12px;border-radius:6px;margin:16px 0;">
          <p style="margin:0 0 8px;font-weight:600;color:#0f172a;">Discharge Documents Uploaded: ${docs.length}</p>
          <ul style="margin:0 0 0 18px;padding:0;">
            ${docs.slice(0, 10).map(d => `<li style="margin:4px 0;">${d.title || d.fileName || 'Document'} (${d.type || 'N/A'})</li>`).join('')}
            ${docs.length > 10 ? `<li style="margin:4px 0;color:#64748b;">...and ${docs.length - 10} more</li>` : ''}
          </ul>
        </div>
        <p style="margin:12px 0 0;font-size:12px;color:#64748b;">Please review the discharge documents and proceed with the approval workflow.</p>
        ${patientLink}
      </body></html>`;
      
      await sendEmail({ to: 'doctors@samhealth.co.in', subject, html });
      console.log('Discharge initiation email sent to doctors team');
    } catch (e) {
      console.warn('Non-blocking email failed (initiateDischargeReview):', e?.message || e);
    }
    
    return { success: true };
  } catch (e) {
    console.error('Error initiating discharge review:', e);
    return { success: false, error: e.message };
  }
};

// Maker-Checker review flow for Scrutiny
// Maker records a review pass with optional note
export const recordScrutinyMakerReview = async (applicationId, { makerId, makerName = '', note = '' } = {}) => {
  try {
    const res = await getApplication(applicationId);
    if (!res.success) return res;
    const appRef = doc(db, 'patientApplications', res.application.id);
    await updateDoc(appRef, {
      'workflow.dischargeSettlement.scrutinyReview.maker': {
        id: makerId || 'hospital',
        name: makerName || 'Hospital',
        note: (note || '').trim(),
        at: new Date(),
        status: 'reviewed'
      },
      updatedAt: new Date()
    });
    return { success: true };
  } catch (e) {
    console.error('Error recording scrutiny maker review:', e);
    return { success: false, error: e.message };
  }
};

// Checker confirms the review; on confirm, we can flip a flag to enable submission
export const recordScrutinyCheckerConfirm = async (applicationId, { checkerId, checkerName = '', note = '' } = {}) => {
  try {
    const res = await getApplication(applicationId);
    if (!res.success) return res;
    const appRef = doc(db, 'patientApplications', res.application.id);
    await updateDoc(appRef, {
      'workflow.dischargeSettlement.scrutinyReview.checker': {
        id: checkerId || 'hospital_checker',
        name: checkerName || 'Hospital Checker',
        note: (note || '').trim(),
        at: new Date(),
        status: 'confirmed'
      },
      'workflow.dischargeSettlement.scrutinyReview.status': 'checker_confirmed',
      updatedAt: new Date()
    });

    // Update bucket filter - scrutiny approved
    await updateRoleBucket(res.application.id, 'doctor', 'scrutiny_approved');

    return { success: true };
  } catch (e) {
    console.error('Error recording scrutiny checker confirmation:', e);
    return { success: false, error: e.message };
  }
};

// Derive hospital-facing stage for bifurcation tabs
// Returns an array of stages that the application belongs to
const deriveStageForHospital = (application) => {
  try {
    const wf = application.workflow || {};
    const med = wf.medicalVerification || {};
    const ds = wf.dischargeSettlement || {};
    const stages = [];

    // 1) Query bucket (any active dispute, pending clarification, KYP challenged by hospital)
    const hasActiveDispute =
      application.status === 'dispute_pending' ||
      application.status === 'pending_clarification' ||
      med.status === 'pending_info' ||
      (Array.isArray(application.activeDisputes) && application.activeDisputes.length > 0) ||
      application.disputeStatus === 'active' ||
      (med.kypHospitalAcknowledgement && med.kypHospitalAcknowledgement.status === 'challenged');
    if (hasActiveDispute) stages.push('query');

    // 2) Scrutiny/Dispatch buckets around FAL
    const hasDispatchDocs = Array.isArray(application.dispatchDocuments) && application.dispatchDocuments.length > 0;
    const falPublished = !!ds.finalApprovalLetterUrl;
    const falId = ds.finalApprovalLetterId;
    const falAck = falId && wf.acknowledgements ? wf.acknowledgements[falId]?.status : undefined; // 'accepted' | 'challenged' | 'pending'
    const scrutinyCleared = (wf.dischargeSettlement?.scrutinyReview?.status === 'checker_confirmed') || (wf.dischargeSettlement?.scrutinyApproval?.status === 'approved');

    // If FAL is published (and ideally accepted), show case in Scrutiny until scrutiny is cleared
    if (falPublished) {
      if (!scrutinyCleared && (falAck === 'accepted' || !falAck)) {
        stages.push('scrutiny');
      }
      // Once scrutiny cleared or any dispatch docs exist, move to Dispatch bucket
      if (scrutinyCleared || hasDispatchDocs) stages.push('dispatch');
    }

    // 3) KYP path states
    // 3a) If hospital has accepted KYP, move to Discharge Pending bucket
    const kypAck = med.kypHospitalAcknowledgement?.status;
    if (kypAck === 'accepted') stages.push('discharge_pending');

    // 3b) KYP Approved by doctor (before hospital acceptance)
    const doctorKypApproved = (med.status === 'approved');
    if (doctorKypApproved) stages.push('kyp_approved');

    // 4) Scrutiny bucket (discharge docs uploaded and likely under review) when FAL not yet published
    const dischargeDocs = Array.isArray(application.dischargeDocuments) ? application.dischargeDocuments : [];
    if (!falPublished && dischargeDocs.length > 0) stages.push('scrutiny');

    // 5) Discharge bucket (discharge initiated)
    const dischargeInitiated = !!(application.medical && (application.medical.dischargeDate || med.dischargeReview || med.dischargeReviewCompleted));
    if (dischargeInitiated) stages.push('discharge');

    // 6) KYP pending if generated and not yet accepted/approved, or default early stage
    const kypGenerated = !!med.kypGenerated || !!med.kypUrl || !!application.kypLetterGenerated || !!med.kypData;
    if (kypGenerated) stages.push('kyp_pending');

    // If no stages were added, default to KYP pending for early-stage applications
    if (stages.length === 0) stages.push('kyp_pending');

    return stages;
  } catch (e) {
    // Safe default
    return ['kyp_pending'];
  }
};

// Hospital-facing display status helper
// Returns one of keys: initiated, query, kyp_approved, discharge_pending, final_approved, dispatch_pending, claimed
// with human labels requested by hospital
export const getHospitalDisplayStatus = (application) => {
  try {
    const w = application?.workflow || {};
    const mv = w.medicalVerification || {};
    const ds = w.dischargeSettlement || {};
    const dispatch = w.dispatch || w.dispatches || null;

    // 1) Query Cases - highest priority
    const hasQuery = (
      application?.status === 'dispute_pending' ||
      application?.status === 'pending_clarification' ||
      mv?.status === 'pending_info' ||
      application?.disputeStatus === 'active' ||
      (Array.isArray(application?.activeDisputes) && application.activeDisputes.length > 0) ||
      mv?.kypHospitalAcknowledgement?.status === 'challenged'
    );
    if (hasQuery) return { key: 'query', label: 'Query Cases' };

    // 2) Claimed Cases - after dispatch/disbursal
    const disb = w.disbursal || {};
    const hasDispatchRecords = Array.isArray(dispatch) ? dispatch.length > 0 : !!dispatch?.status;
    const hasDispatchDocs = Array.isArray(application?.dispatchDocuments) && application.dispatchDocuments.length > 0;
    if (disb?.status || hasDispatchRecords || hasDispatchDocs) {
      return { key: 'claimed', label: 'Claimed Cases' };
    }

    // 3) Final Approved vs Dispatch Pending
    const hasFinalApproval = !!ds?.finalApprovalLetterUrl;
    if (hasFinalApproval) {
      // Final Approved if approval exists and NO dispatch/disbursal yet
      const hasAnyDispatchOrDisbursal = disb?.status || hasDispatchRecords || hasDispatchDocs;
      if (!hasAnyDispatchOrDisbursal) {
        return { key: 'final_approved', label: 'Final Approved' };
      }
      // Otherwise, awaiting dispatch actions
      return { key: 'dispatch_pending', label: 'Dispatch Pending' };
    }

    // 4) KYP/Discharge flow
    // 4a) Hospital accepted KYP -> Discharge Pending
    const kypAck = mv?.kypHospitalAcknowledgement?.status || mv?.kypAck || mv?.kypStatus;
    if (kypAck === 'accepted') {
      return { key: 'discharge_pending', label: 'Discharge Pending' };
    }
    // 4b) Discharge started -> Discharge
    const dischargeStarted = !!(application?.dischargeInitiated || ds?.status || mv?.dischargeReview || (Array.isArray(application?.dischargeDocuments) && application.dischargeDocuments.length > 0));
    if (dischargeStarted) {
      return { key: 'discharge', label: 'Discharge' };
    }
    // 4c) Doctor approved KYP -> KYP Approved
    const doctorKypApproved = (mv?.status === 'approved');
    if (doctorKypApproved) {
      return { key: 'kyp_approved', label: 'KYP Approved' };
    }

    // 5) Initiated Case (default early stage)
    return { key: 'initiated', label: 'Initiated Case' };
  } catch (e) {
    return { key: 'initiated', label: 'Initiated Case' };
  }
};

// Get hospital applications with hospital-stage counts and pagination
export const getHospitalApplicationsBifurcated = async (hospitalId, filters = {}, page = 1, pageSize = 15) => {
  try {
    if (!hospitalId) return { success: false, error: 'Missing hospitalId', applications: [], total: 0, stageCountsHospital: {} };

    // Base query
    let qBase = collection(db, 'patientApplications');
    qBase = query(qBase, where('hospitalId', '==', hospitalId));

    // Apply simple Firestore-side filters to reduce dataset
    if (filters.status) {
      qBase = query(qBase, where('status', '==', filters.status));
    }
    if (filters.emergency !== undefined && filters.emergency !== '') {
      const isEmergency = typeof filters.emergency === 'string' ? filters.emergency === 'true' : !!filters.emergency;
      qBase = query(qBase, where('emergency', '==', isEmergency));
    }
    if (filters.priority) {
      qBase = query(qBase, where('priority', '==', filters.priority));
    }
    if (filters.treatmentType) {
      // support incoming keys like 'medical.treatmentType' or shorthand 'treatmentType'
      const tType = filters['medical.treatmentType'] || filters.treatmentType;
      if (tType) qBase = query(qBase, where('medical.treatmentType', '==', tType));
    }

    // Order newest first to keep UI consistent
    qBase = query(qBase, orderBy('submittedAt', 'desc'));

    const snap = await getDocs(qBase);
    let apps = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

    // Client-side filters: searchTerm, date range, doctor, insurer, min/max cost, etc.
    const searchTerm = (filters.searchTerm || '').toString().trim().toLowerCase();
    if (searchTerm) {
      apps = apps.filter((data) => {
        const searchableText = `\n${data.applicationId}\n${data.patient?.name}\n${data.medical?.diagnosis}\n${data.policy?.number}\n${data.medical?.doctor?.name}\n`.toLowerCase();
        return searchableText.includes(searchTerm);
      });
    }

    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      apps = apps.filter((app) => {
        const submitted = app.submittedAt?.toDate ? app.submittedAt.toDate() : (app.submittedAt ? new Date(app.submittedAt) : null);
        return submitted ? submitted >= fromDate : true;
      });
    }
    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      apps = apps.filter((app) => {
        const submitted = app.submittedAt?.toDate ? app.submittedAt.toDate() : (app.submittedAt ? new Date(app.submittedAt) : null);
        return submitted ? submitted <= toDate : true;
      });
    }
    if (filters.doctorName) {
      const dn = filters.doctorName.toString().toLowerCase();
      apps = apps.filter((app) => (app.medical?.doctor?.name || '').toLowerCase().includes(dn));
    }
    if (filters.insuranceCompany) {
      const ic = filters.insuranceCompany.toString().toLowerCase();
      apps = apps.filter((app) => (app.policy?.insuranceCompany || '').toLowerCase().includes(ic));
    }
    if (filters.minCost) {
      const min = parseFloat(filters.minCost) || 0;
      apps = apps.filter((app) => (parseFloat(app.medical?.estimatedCost) || 0) >= min);
    }
    if (filters.maxCost) {
      const max = parseFloat(filters.maxCost) || Number.MAX_SAFE_INTEGER;
      apps = apps.filter((app) => (parseFloat(app.medical?.estimatedCost) || 0) <= max);
    }

    // Compute stage counts
    const stageCountsHospital = apps.reduce((acc, app) => {
      const stages = deriveStageForHospital(app);
      stages.forEach(stage => {
        acc[stage] = (acc[stage] || 0) + 1;
      });
      return acc;
    }, {});
    // Ensure all expected keys exist and add an 'all' total
    const expectedKeys = ['kyp_pending', 'kyp_approved', 'query', 'discharge_pending', 'discharge', 'scrutiny', 'dispatch'];
    expectedKeys.forEach((k) => { if (!stageCountsHospital[k]) stageCountsHospital[k] = 0; });
    stageCountsHospital.all = apps.length;

    // Stage filter
    if (filters._hospitalStage && filters._hospitalStage !== 'all') {
      apps = apps.filter((app) => {
        const stages = deriveStageForHospital(app);
        return stages.includes(filters._hospitalStage);
      });
    }

    const total = apps.length;
    const startIndex = Math.max(0, (page - 1) * pageSize);
    const paginated = apps.slice(startIndex, startIndex + pageSize);

    return {
      success: true,
      applications: paginated,
      total,
      page,
      pageSize,
      totalPages: Math.ceil(total / pageSize) || 1,
      stageCountsHospital,
    };
  } catch (error) {
    console.error('Error fetching hospital applications (bifurcated):', error);
    return {
      success: false,
      error: error.message,
      applications: [],
      total: 0,
      stageCountsHospital: {},
    };
  }
};

// Internal helper to resolve a Firestore document reference by the external applicationId
// Returns { success: boolean, ref?, error? }
export const getApplicationDocRef = async (applicationId) => {
  try {
    const result = await getApplication(applicationId);
    if (!result.success) {
      return { success: false, error: 'Application not found' };
    }
    const applicationRef = doc(db, 'patientApplications', result.application.id);
    return { success: true, ref: applicationRef };
  } catch (e) {
    return { success: false, error: e.message };
  }
};

// Update policy information (and related employment fields) for an application
// Accepts a partial payload; only provided fields will be updated
export const updatePolicyInformation = async (applicationId, policyPayload = {}, employmentPayload = {}) => {
  try {
    const refResult = await getApplicationDocRef(applicationId);
    if (!refResult.success) {
      return { success: false, error: refResult.error || 'Application not found' };
    }

    // Build update object with proper nested field paths
    const updateData = { updatedAt: new Date() };

    // Policy fields mapping
    const policyMap = {
      number: policyPayload.policyNumber,
      insuranceCompany: policyPayload.insuranceCompany,
      type: policyPayload.policyType,
      coverageAmount: policyPayload.coverageAmount,
      startDate: policyPayload.startDate,
      endDate: policyPayload.endDate,
      tpaName: policyPayload.tpaName,
    };

    Object.entries(policyMap).forEach(([key, val]) => {
      if (typeof val !== 'undefined' && val !== null && val !== '') {
        // Normalize number field
        const isNumericKey = key === 'coverageAmount';
        updateData[`policy.${key}`] = isNumericKey ? Number(val) : val;
      }
    });

    // Employment fields mapping (lives under application.employment)
    const employmentMap = {
      status: employmentPayload.employmentStatus,
      workingCompanyName: employmentPayload.workingCompanyName,
      monthlyIncome: employmentPayload.monthlyIncome,
      designation: employmentPayload.designation,
      natureOfWork: employmentPayload.natureOfWork,
    };

    Object.entries(employmentMap).forEach(([key, val]) => {
      if (typeof val !== 'undefined' && val !== null && val !== '') {
        const isNumericKey = key === 'monthlyIncome';
        updateData[`employment.${key}`] = isNumericKey ? Number(val) : val;
      }
    });

    await updateDoc(refResult.ref, updateData);
    return { success: true };
  } catch (error) {
    console.error('Error updating policy information:', error);
    return { success: false, error: error.message || 'Failed to update policy information' };
  }
};

// Update patient stay dates (admission/discharge) and mark confirmation
export const updatePatientStayDates = async (applicationId, { admissionDate, dischargeDate }, updatedByName = '') => {
  try {
    // Find application doc by applicationId
    const qRef = query(
      collection(db, 'patientApplications'),
      where('applicationId', '==', applicationId)
    );
    const snap = await getDocs(qRef);
    if (snap.empty) {
      return { success: false, error: 'Application not found' };
    }
    const docRef = snap.docs[0].ref;

    const updatePayload = {
      'medical.admissionDate': admissionDate || null,
      'medical.dischargeDate': dischargeDate || null,
      updatedAt: new Date(),
      'workflow.dischargeSettlement.stayDatesConfirmation': {
        status: 'confirmed',
        at: new Date(),
        byName: updatedByName || ''
      }
    };

    await updateDoc(docRef, updatePayload);
    return { success: true };
  } catch (error) {
    console.error('Error updating stay dates:', error);
    return { success: false, error: error.message || 'Failed to update stay dates' };
  }
};